import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import EnergyVisualizer from "./ER";
import EasySpeech from 'easy-speech';
import requestQueue from "./RequestQueue";
import screenRecordingManager from "./ScreenRecordingManager";
// Speech-to-Text is now provided via an adapter facade so we can swap vendors
// (Azure / Sarvam / etc.) without touching this component. See services/speech/.
import { createSpeechProvider, fetchActiveProviderName } from "../../services/speech/speechService";
// import CheatingDetection from './CheatingDetection';
// import EnhancedCheatingDetection from './EnhancedCheatingDetection';
import { isDesktopApp, stopDesktopProctoring, logCheatingEvent, submitProctoringData, uploadRecording } from "../../utils/desktopBridge";
import { ensureEntireScreenSelected, ENTIRE_SCREEN_REQUIRED_MESSAGE } from "../../utils/screenShare";
import DesktopViolationWarning from "../../components/DesktopViolationWarning";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import SmartSilenceDetector from "../../services/vad/smartSilenceDetector";
import VoiceWaveform from "./VoiceWaveform";
import ProctoringMount from "../components/ProctoringMount";

// shadcn components
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

// Lucide icons
import { 
  Mic, 
  Video, 
  PhoneOff, 
  MessageSquare, 
  Clock,
  AlertTriangle,
  X
} from "lucide-react";

// Module-level lock to prevent duplicate audio initialization in React Strict Mode
// This persists across component remounts, unlike useRef which gets recreated
let globalAudioLock = false;

// Session key for additional protection - tracks active audio session
const AUDIO_SESSION_LOCK_KEY = 'question_section_audio_lock';

// Module-level audio context to persist across strict mode remounts
let globalAudioContext = null;
let globalSourceNode = null;
let globalAnalyserNode = null;
// Module-level mic stream — reused across question transitions and shared
// with the VAD detector so we don't open a second getUserMedia session.
let globalMicStream = null;

// NOTE: Azure-specific module globals (globalRecognizer, globalAudioStream,
// globalRecognizerInitialized) and the Azure SDK imports/speechConfig now live
// inside services/speech/azureAdapter.js. This component no longer knows or
// cares which STT vendor is active.

function QuestionSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasEnded, setHasEnded] = useState(false);
  // Webcam stream shared with the proctoring engines (reuses the same camera
  // startStreaming() already opens for face recording — no second getUserMedia).
  const [proctorStream, setProctorStream] = useState(null);
  const [showConfirmEndModal, setShowConfirmEndModal] = useState(false);
  // When true, the candidate has finished the last question. Button switches to "Submit Interview"
  // and bypasses the confirmation modal. Used instead of fragile auto-end via useEffect chain.
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [dummyState, setDummyState] = useState(0); // Dummy state to trigger useEffect
  const currentQuestionIndexRef = useRef(0); // Use ref instead of state
  const technicalQuestionIndexRef = useRef(0); // Use ref instead of state
  const [question, setQuestion] = useState([]);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  let stream;
  const transcriptionRef = useRef("");  // Use ref instead of state
  // const tempTranscriptionRef = useRef(""); // Temporary transcription storage
  const audioStreamRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false); // State to track if the question is being spoken
  const resumeQuestionsRef = useRef(0); // State to store questions about resume and feedback
  const countdownIntervalRef = useRef(null); // To manage the countdown interval
  const retryAttemptsRef = useRef(0); // To track the number of retry attempts
  const moreTimeRef = useRef(0);
  // const moreTimeTranscriptionRef = useRef(null);
  const currentQuestionRef = useRef(null);
  // Global flag to control the interview process
  const interviewActiveRef = useRef(true);
  const [userName, setUserName] = useState(null); // State to store the user name
  const mediaRecorderRef = useRef(null);
  const recordingDurationRef = useRef(0);
  const recordingTimeoutRef = useRef(null);

  // ── Session-level audio recording (full interview, for AudioAnalysis) ───
  const sessionRecorderRef = useRef(null);   // runs whole interview
  const sessionChunksRef   = useRef([]);
  const sessionMimeRef     = useRef('audio/webm');
  const [cheatingAlert, setCheatingAlert] = useState(false);
  const [showSpecialKeyWarning, setShowSpecialKeyWarning] = useState(false);
  const [specialKeyAttempts, setSpecialKeyAttempts] = useState(0);
  const [showRightClickWarning, setShowRightClickWarning] = useState(false);
  const [rightClickAttempts, setRightClickAttempts] = useState(0);
  const [pasteAttempts, setPasteAttempts] = useState(0);
  const [showPasteWarning, setShowPasteWarning] = useState(false);
  const [isDynamicTest, setIsDynamicTest] = useState(false);
  const [maxQuestions, setMaxQuestions] = useState(15);
  const conversationHistoryRef = useRef([]);
  const [isFetchingNextQuestion, setIsFetchingNextQuestion] = useState(false);
  const lastAnswerRef = useRef("");
  const monitoringActiveRef = useRef(false);

  // Screen recording refs
  const screenStreamRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const screenChunksRef = useRef([]);
  
  // Screen recording state
  const [isScreenRecording, setIsScreenRecording] = useState(false);
  const [screenSharingActive, setScreenSharingActive] = useState(false);
  const [screenShareError, setScreenShareError] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isTabActive, setIsTabActive] = useState(true);
  const [switchCount, setSwitchCount] = useState(0);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [showTabSwitchWarning, setShowTabSwitchWarning] = useState(false);
  const [exitTimer, setExitTimer] = useState(30);
  const exitTimerRef = useRef(null);
  const [showFullscreenWarning, setShowFullscreenWarning] = useState(false);
  const [testStarted, setTestStarted] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds — gives candidates enough time for behavioral / technical answers (was 90s previously, too tight for STAR-style responses)
  const timerRef = useRef(null);
  const micStreamRef = useRef(null);
  const isProcessingNextQuestionRef = useRef(false);

  // Speech-to-Text state — refs/setters are passed into the active provider
  // (azure | sarvam) at init time. The wrapper functions below delegate to it.
  const recognizerRef = useRef(null);
  const [recognisingText, setRecognisingText] = useState("");
  // Ref mirror of recognisingText so functions running inside setInterval /
  // setTimeout closures (askForMoreTime, handleNoResponse, …) can read the
  // *latest* interim text without going stale. transcriptionRef only holds
  // the FINAL text (populated on Azure's `recognized` event, which fires
  // only after a pause), so a candidate speaking continuously for 10+ s
  // would otherwise look silent to those pollers.
  const recognisingTextRef = useRef("");
  const [isRecognising, setIsRecognising] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [transcription, setTranscription] = useState("");
  const recognitionActiveRef = useRef(false);
  const recognitionRetryCount = useRef(0);
  // Holds the active SpeechProvider instance (built once per mount in the
  // initialization useEffect below). null until the first init runs.
  const providerRef = useRef(null);

  // Network monitoring (using shared hook)
  const { networkStatus, networkSpeed, NetworkIcon } = useNetworkStatus();

  const chatBodyRef = useRef(null);
  const [convTick, setConvTick] = useState(0);
  const conversationRef = useRef([]);

  const thinkingTimeoutRef = useRef(null);

  // UI state - collapsible transcript panel
  const [showTranscript, setShowTranscript] = useState(true);

  // Cheating detection handlers
  const [eyeTrackingWarnings, setEyeTrackingWarnings] = useState(0);
  const [multipleFacesWarnings, setMultipleFacesWarnings] = useState(0);
  const [showEyeTrackingWarning, setShowEyeTrackingWarning] = useState(false);
  const [showMultipleFacesWarning, setShowMultipleFacesWarning] = useState(false);
  const [showFaceNotVisibleWarning, setShowFaceNotVisibleWarning] = useState(false);
  const [faceNotVisibleTimer, setFaceNotVisibleTimer] = useState(30);
  const faceNotVisibleTimerRef = useRef(null);
  const [isCheatingDetectionActive, setIsCheatingDetectionActive] = useState(false);
  const [phoneDetectionWarnings, setPhoneDetectionWarnings] = useState(0);
  const [suspiciousObjectWarnings, setSuspiciousObjectWarnings] = useState(0);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  const [showSuspiciousObjectWarning, setShowSuspiciousObjectWarning] = useState(false);
  const [headPoseWarnings, setHeadPoseWarnings] = useState(0);
  const [showHeadPoseWarning, setShowHeadPoseWarning] = useState(false);
  const [headPoseDirection, setHeadPoseDirection] = useState('');

useEffect(() => {
  const isThinking = !isSpeaking && !isRecording;

  // If we ENTER thinking → start watchdog timer
  if (isThinking) {
    if (!thinkingTimeoutRef.current) {
      thinkingTimeoutRef.current = setTimeout(() => {
        console.warn("⏱ Watchdog: stuck for 45s → force next question (flag was:", isProcessingNextQuestionRef.current, ")");
        // Force-reset the processing flag regardless — this is a recovery path.
        // If isProcessingNextQuestionRef is stuck true (e.g. stopAzureRecognition hung),
        // the normal check would silently skip handleNextQuestion and the interview
        // would be frozen forever. Always advance here.
        isProcessingNextQuestionRef.current = false;
        handleNextQuestion();
      }, 45 * 1000); // 45 seconds — short enough to not feel broken
    }
  }
  // If we EXIT thinking → cancel timer
  else {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = null;
    }
  }

  // Cleanup on unmount
  return () => {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = null;
    }
  };
}, [isSpeaking, isRecording]);

const handleCheatingViolation = async (violationType, message) => {
  console.log(`Cheating violation detected: ${violationType} - ${message}`);

  // Log to desktop proctoring if running in desktop app
  if (isDesktopApp()) {
    const severity = ['multiple_faces', 'phone_detected', 'no_face'].includes(violationType) ? 'high' : 'medium';
    logCheatingEvent(violationType, severity, {
      message,
      timestamp: new Date().toISOString()
    });
  }
  
  if (violationType === 'eye_tracking') {
    const newCount = eyeTrackingWarnings + 1;
    setEyeTrackingWarnings(newCount);
    setShowEyeTrackingWarning(true);
    
    // await speak("Please keep your eyes on the screen during the assessment.");
    
    // if (newCount >= 3) {
    //   await speak("Multiple eye tracking violations detected. This assessment will be terminated due to proctoring policy violations.");
    //   forceZeroMarksSubmission();
    //   return;
    // }
    
    setTimeout(() => setShowEyeTrackingWarning(false), 5000);
  } 
  else if (violationType === 'multiple_faces') {
    const newCount = multipleFacesWarnings + 1;
    setMultipleFacesWarnings(newCount);
    setShowMultipleFacesWarning(true);
    
    // await speak("Multiple people detected in the frame. Please ensure you are alone during the assessment.");
    
    // if (newCount >= 3) {
    //   await speak("Multiple people detected repeatedly. This assessment will be terminated due to proctoring policy violations.");
    //   forceZeroMarksSubmission();
    //   return;
    // }
    
    setTimeout(() => setShowMultipleFacesWarning(false), 5000);
  }
  else if (violationType === 'phone_detected') {
    const newCount = phoneDetectionWarnings + 1;
    setPhoneDetectionWarnings(newCount);
    setShowPhoneWarning(true);
    
    // More strict for phone detection
    // await speak("Mobile phone detected in the frame. Please remove any electronic devices from view immediately.");
    
    // if (newCount >= 2) { // Only 2 warnings for phone
    //   await speak("Mobile phone detected repeatedly. This is a serious violation. This assessment will be terminated due to proctoring policy violations.");
    //   forceZeroMarksSubmission();
    //   return;
    // }
    
    setTimeout(() => setShowPhoneWarning(false), 7000); // Longer warning
  }
  else if (violationType === 'suspicious_object') {
    const newCount = suspiciousObjectWarnings + 1;
    setSuspiciousObjectWarnings(newCount);
    setShowSuspiciousObjectWarning(true);
    
    // await speak("Suspicious object detected in the frame. Please ensure your workspace is clear of unauthorized materials.");
    
    // if (newCount >= 3) {
    //   await speak("Suspicious objects detected repeatedly. This assessment will be terminated due to proctoring policy violations.");
    //   forceZeroMarksSubmission();
    //   return;
    // }
    
    setTimeout(() => setShowSuspiciousObjectWarning(false), 5000);
  }
  else if (violationType === 'head_pose') {
    const newCount = headPoseWarnings + 1;
    setHeadPoseWarnings(newCount);
    setShowHeadPoseWarning(true);
    
    // Extract direction from message
    const direction = message.replace('Head turned ', '');
    setHeadPoseDirection(direction);
    
    // Specific warnings based on direction
    // if (direction.includes('downward')) {
    //   await speak("Please keep your head up and look at the screen. Looking down is not permitted during the assessment.");
    // } else if (direction.includes('sideways') || direction.includes('left') || direction.includes('right')) {
    //   await speak("Please face the screen directly. Turning your head away is not permitted during the assessment.");
    // } else if (direction.includes('upward')) {
    //   await speak("Please look at the screen. Looking away is not permitted during the assessment.");
    // } else {
    //   await speak("Please maintain proper head position facing the screen during the assessment.");
    // }
    
    // if (newCount >= 3) {
    //   await speak("Multiple head position violations detected. This assessment will be terminated due to proctoring policy violations.");
    //   forceZeroMarksSubmission();
    //   return;
    // }
    
    setTimeout(() => setShowHeadPoseWarning(false), 7000);
  }
};

// Add this function to handle face not visible
const handleFaceNotVisible = async () => {
  // Stop recording temporarily
  const wasRecording = isRecording;
  if (wasRecording) {
    await stopRecordingWithoutHandleNext();
  }
  
  setShowFaceNotVisibleWarning(true);
  
  // Play warning audio
  await speak("Your face is not visible. Please adjust your camera position and ensure proper lighting. You have 30 seconds to make yourself visible.");
  
  // Start 30-second countdown
  let timeLeft = 30;
  setFaceNotVisibleTimer(timeLeft);
  
  faceNotVisibleTimerRef.current = setInterval(() => {
    timeLeft--;
    setFaceNotVisibleTimer(timeLeft);
    
    if (timeLeft <= 0) {
      clearInterval(faceNotVisibleTimerRef.current);
      handleFaceStillNotVisible();
    }
  }, 1000);
};

// Add this function to handle face still not visible after timeout
const handleFaceStillNotVisible = async () => {
  setShowFaceNotVisibleWarning(false);
  
  // Check if face is now visible by checking the last detection
  // If still not visible, end interview
  await speak("Your face is still not visible. This assessment will be terminated due to proctoring issues.");
  
  interviewActiveRef.current = false;
  forceZeroMarksSubmission();
};

// Add this function to handle when face becomes visible again
const handleFaceVisibleAgain = () => {
  if (faceNotVisibleTimerRef.current) {
    clearInterval(faceNotVisibleTimerRef.current);
    setShowFaceNotVisibleWarning(false);
    setFaceNotVisibleTimer(30);
    
    // Resume recording if it was active
    if (!isRecording && testStarted && interviewActiveRef.current) {
      startRecording();
    }
  }
};

// COMPREHENSIVE INPUT RESTRICTIONS - Add this useEffect
useEffect(() => {
  // Function to pause current speech and play warning
//   const playWarningWithPriority = async (warningText) => {
//     // Store the current speech progress
//     const currentProgress = {...speechProgressRef.current};
//     const currentToken = currentSpeechTokenRef.current;
//     const currentQuestion = currentQuestionRef.current;
//     const wasInQuestionSpeech = isSpeaking && currentQuestion;
    
//     // Cancel any current speech immediately
//     if (currentAudioRef.current) {
//       currentAudioRef.current.pause();
//       currentAudioRef.current = null;
//     }

//     // Increment token to prevent old speech from resuming
//     currentSpeechTokenRef.current++;
    
//     // Play the warning
//     await speak(warningText);

//     // If we were in the middle of speaking a question, resume from where we left off
//   if (wasInQuestionSpeech && interviewActiveRef.current && currentQuestion && 
//     currentProgress.currentSentenceIndex < currentProgress.totalSentences) {
  
//   // Wait a brief moment before resuming
//   await new Promise(resolve => setTimeout(resolve, 500));
  
//   // Resume from the interrupted point
//   const remainingText = currentProgress.sentences
//     .slice(currentProgress.currentSentenceIndex)
//     .join(' ');
  
//   if (remainingText.trim()) {
//     await speak(remainingText);
//     if (!interviewActiveRef.current) return;
//     transcriptionRef.current = "";
//     SILENCE_DURATION = 5000;
//     await startRecording();
//   }
// }
    
//     // Resume the interview flow if it was interrupted
//     // if (wasSpeaking && interviewActiveRef.current) {
//     //   // If we were in the middle of speaking a question, restart it
//     //   if (currentQuestionRef.current && testStarted) {
//     //     await speak(currentQuestionRef.current);
//     //     if (!interviewActiveRef.current) return;
//     //     transcriptionRef.current = "";
//     //     SILENCE_DURATION = 5000;
//     //     await startRecording();
//     //   }
//     // }
//   };

  // Function to disable right-click context menu
  const disableContextMenu = (e) => {
    e.preventDefault();
    const newAttempts = rightClickAttempts + 1;
    setRightClickAttempts(newAttempts);
    setShowRightClickWarning(true);

    // playWarningWithPriority("Right-click is not allowed in this assessment. Multiple attempts may result in disqualification.");
    
    // Speak warning on 3rd attempt
    if (newAttempts >= 3) {
      forceZeroMarksSubmission();
    }
    
    // Hide warning after 3 seconds
    setTimeout(() => {
      setShowRightClickWarning(false);
    }, 5000);
    
    return false;
  };

  // Function to disable special keys (Alt, Windows, F1-F12, etc.)
  const disableSpecialKeys = (e) => {
    const restrictedKeys = {
      // Alt keys
      18: 'Alt', // Left and Right Alt
      // Windows keys
      91: 'Windows Left', 
      92: 'Windows Right',
      93: 'Windows Menu',
      // Function keys
      // 123: 'F12',
    };

    if (restrictedKeys[e.keyCode]) {
      e.preventDefault();
      const newAttempts = specialKeyAttempts + 1;
      setSpecialKeyAttempts(newAttempts);
      setShowSpecialKeyWarning(true);

      // playWarningWithPriority(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);
      
      // Speak warning on 3rd attempt
      if (newAttempts >= 3) {
        forceZeroMarksSubmission();
      }
      
      // Hide warning after 3 seconds
      setTimeout(() => {
        setShowSpecialKeyWarning(false);
      }, 5000);
      
      return false;
    }

    // Detect Ctrl+Alt+Del (though this is often handled at OS level)
    if (e.ctrlKey && e.altKey && e.keyCode === 46) { // Delete key
      e.preventDefault();
      const newAttempts = specialKeyAttempts + 1;
      setSpecialKeyAttempts(newAttempts);
      setShowSpecialKeyWarning(true);

      // playWarningWithPriority(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);

      // Speak warning on 3rd attempt
      if (newAttempts >= 3) {
        forceZeroMarksSubmission();
      }

      setTimeout(() => setShowSpecialKeyWarning(false), 5000);
      return false;
    }

    // Detect Alt+Tab (tab switching)
    if (e.altKey && e.keyCode === 9) {
      e.preventDefault();
      const newAttempts = specialKeyAttempts + 1;
      setSpecialKeyAttempts(newAttempts);
      setShowSpecialKeyWarning(true);

      // playWarningWithPriority(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);

      // Speak warning on 3rd attempt
      if (newAttempts >= 3) {
        forceZeroMarksSubmission();
      }

      setTimeout(() => setShowSpecialKeyWarning(false), 5000);
      return false;
    }

    // Detect Alt+F4 (window close)
    if (e.altKey && e.keyCode === 115) { // F4
      e.preventDefault();
      const newAttempts = specialKeyAttempts + 1;
      setSpecialKeyAttempts(newAttempts);
      setShowSpecialKeyWarning(true);

      // playWarningWithPriority(`The ${restrictedKeys[e.keyCode]} key is not allowed. Multiple attempts may result in disqualification.`);

      // Speak warning on 3rd attempt
      if (newAttempts >= 3) {
        forceZeroMarksSubmission();
      }
      setTimeout(() => setShowSpecialKeyWarning(false), 5000);
      return false;
    }
  };

  // Function to disable keyboard shortcuts for copy/paste
  const disableShortcuts = (e) => {
    // Detect Ctrl+C, Ctrl+V, Ctrl+X, Cmd+C, Cmd+V, Cmd+X
    if ((e.ctrlKey || e.metaKey) && 
        (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'C' || e.key === 'V' || e.key === 'X')) {
      e.preventDefault();
      handlePasteAttempt();
      return false;
    }
    
    // Detect right-click context menu key
    if (e.key === 'ContextMenu') {
      e.preventDefault();
      return false;
    }
  };

  // Function to handle paste attempt
  const handlePasteAttempt = () => {
    const newAttempts = pasteAttempts + 1;
    setPasteAttempts(newAttempts);
    setShowPasteWarning(true);

    // playWarningWithPriority("Copy-paste is not allowed in this assessment. Multiple attempts may result in disqualification.");
    
    // Speak warning on 3rd attempt
    if (newAttempts >= 3) {
      forceZeroMarksSubmission();
    }
    
    // Hide warning after 3 seconds
    setTimeout(() => {
      setShowPasteWarning(false);
    }, 5000);
  };

  // Add all event listeners
  document.addEventListener('contextmenu', disableContextMenu);
  document.addEventListener('keydown', disableSpecialKeys);
  document.addEventListener('keydown', disableShortcuts);
  
  // Also prevent drag and drop of text
  document.addEventListener('dragstart', (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
      e.preventDefault();
    }
  });

  document.addEventListener('drop', (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
      e.preventDefault();
      handlePasteAttempt();
    }
  });

  // Clean up all event listeners
  return () => {
    document.removeEventListener('contextmenu', disableContextMenu);
    document.removeEventListener('keydown', disableSpecialKeys);
    document.removeEventListener('keydown', disableShortcuts);
    document.removeEventListener('dragstart', (e) => {});
    document.removeEventListener('drop', (e) => {});
  };
}, [pasteAttempts, specialKeyAttempts, rightClickAttempts]);

// Monitor for serious violations that trigger auto-submission
useEffect(() => {
  const totalViolations = pasteAttempts + specialKeyAttempts + rightClickAttempts + switchCount + fullscreenExitCount;
  
  if (totalViolations >= 10) { // Auto-submit after 10 total violations
    forceZeroMarksSubmission();
  }
}, [pasteAttempts, specialKeyAttempts, rightClickAttempts, switchCount, fullscreenExitCount]);
  
// ── Speech-to-Text wrappers ─────────────────────────────────────────────────
// These thin wrappers delegate to the active provider held in providerRef.
// Keeping the original function names (`startAzureRecognition`, etc.) means
// the 15+ call sites in this component don't need to change. Despite the
// "Azure"-prefixed names, the underlying provider may be Azure OR Sarvam —
// whatever the super admin has selected in the Services page.
//
// The provider itself is built once in the initialization useEffect below,
// after we fetch the active provider name from the platform settings.

const createNewRecognizer             = async ()         => providerRef.current ? providerRef.current.createNewRecognizer()             : undefined;
const reinitializeAndStartRecognition = async ()         => providerRef.current ? providerRef.current.reinitializeAndStartRecognition() : undefined;
const startAzureRecognition           = async ()         => providerRef.current ? providerRef.current.startRecognition()                : undefined;
const stopAzureRecognition            = async ()         => providerRef.current ? providerRef.current.stopRecognition()                 : undefined;
const restartRecognition              = async ()         => providerRef.current ? providerRef.current.restart()                         : undefined;
const cleanupRecognizer               = async (force = false) => providerRef.current ? providerRef.current.cleanup({ force })           : undefined;

// ── Initialize the active STT provider when the test starts ─────────────────
// On first mount we fetch the platform's stt_provider setting and build the
// matching adapter (azure | sarvam). On a React Strict Mode remount the
// adapter's module-level state (recognizer / stream / initialized flag)
// persists, so reuseExisting() reattaches our refs to the original recognizer.
useEffect(() => {
  const initializeSTT = async () => {
    // Build the provider once per component instance. The factory captures
    // refs/setters at this moment; refs are stable for the component's
    // lifetime so the captures remain valid for every event the adapter fires.
    if (!providerRef.current) {
      const providerName = await fetchActiveProviderName();
      console.log('[stt] Active provider:', providerName);

      providerRef.current = createSpeechProvider(providerName, {
        recognizerRef,
        audioStreamRef,
        recognitionActiveRef,
        recognitionRetryCount,
        interviewActiveRef,
        transcriptionRef,
        setIsRecognising,
        setRecognisingText,
        setTranscription,
        setErrorMessage,
        setConvTick,
        onUserMessage: addUserMessage,
      });
    }

    // Reuse the existing recognizer if React Strict Mode just remounted us.
    // The adapter's module-level globals (e.g. _globalRecognizer in
    // azureAdapter.js) survive remounts and contain the still-live recognizer.
    const reused = providerRef.current.reuseExisting();
    if (reused) return;

    await providerRef.current.createNewRecognizer();
  };

  if (testStarted) {
    initializeSTT();
    setIsCheatingDetectionActive(true);
  }

  return () => {
    // Don't cleanup recognizer during React Strict Mode - only on real unmount.
    // The adapter's reuseExisting() will reattach us on remount.
    console.log('[stt] useEffect cleanup - NOT cleaning up recognizer (Strict Mode protection)');
    setIsCheatingDetectionActive(false);
  };
}, [testStarted]);

  const speechRecognitionRef = useRef(null);
  const requestMadeRef = useRef(false);
  const liveMonitoringTimeoutRef = useRef(null);
  const liveMonitoringIntervalRef = useRef(null);
  const nonStopLiveMonitoringIntervalRef = useRef(null);
  const moreAreYouThereRef = useRef(0);        // per-question "are you there?" count (resets each Q)
  const recordingMoreTimeoutRef = useRef(false);

  // ── "More time" / "Are you there?" poll cancellation ────────────────────
  // askForMoreTime / handleNoResponse / askForMoreTimeAfterTimeout each spin
  // up a setInterval that polls transcriptionRef + recognisingTextRef every
  // 500 ms. The previous implementation did not track those intervals, so
  // when handleNextQuestion advanced the interview while one of them was
  // still polling, the interval kept firing — it would eventually escalate
  // to handleNoResponse and play "Are you there?" *over* the next question
  // being read out. This ref lets stopMonitorAudio / handleNextQuestion
  // cancel any pending poll and resolve its Promise so nothing hangs.
  const moreTimeCheckIntervalRef = useRef(null);
  const moreTimeCheckResolverRef = useRef(null);
  // Abort flag: flipped by cancelMoreTimeCheck. Every await step inside
  // askForMoreTime / handleNoResponse / askForMoreTimeAfterTimeout checks
  // this flag AFTER the await and bails out early. Belt-and-suspenders for
  // the case where cancellation happens during audio playback (before the
  // setInterval that moreTimeCheckIntervalRef would catch has even been
  // scheduled).
  const shouldAbortMoreTimeRef = useRef(false);

  // Cleanup helper: clears interval, resolves promise as false, pauses
  // audio. Does NOT touch the abort flag — that's an external signal set
  // only by handleNextQuestion. Called from:
  //   • askForMoreTime / handleNoResponse / askForMoreTimeAfterTimeout —
  //     as a "reset previous state" at the start of a new Promise setup.
  //     These must not set the abort flag, or the new flow aborts itself.
  //   • stopMonitorAudio — defense-in-depth cleanup during teardown.
  //   • cancelMoreTimeCheck — the external cancel entry point (below).
  const clearMoreTimeCheckState = () => {
    if (moreTimeCheckIntervalRef.current) {
      try { clearInterval(moreTimeCheckIntervalRef.current); } catch (_) {}
      moreTimeCheckIntervalRef.current = null;
    }
    if (moreTimeCheckResolverRef.current) {
      // Resolve the awaiting Promise as `false` so the awaiter unblocks
      // and takes the "no more time" branch instead of hanging forever.
      try { moreTimeCheckResolverRef.current(false); } catch (_) {}
      moreTimeCheckResolverRef.current = null;
    }
    // Pause any "more time" / "are you there?" audio that's mid-playback.
    // playAudioAndWait's Promise resolves on the "pause" event as well as
    // "ended", so this actually unblocks the awaiter.
    const clipsToStop = ['audio2', 'audio4', 'audio5', 'audio11', 'audio12'];
    for (const key of clipsToStop) {
      const audio = audioElementsRef.current?.[key];
      if (audio && !audio.paused) {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (_) { /* swallow — best-effort */ }
      }
    }
  };

  // External cancel: call before advancing the interview
  // (handleNextQuestion) to kill any in-flight "more time" / "are you
  // there?" poll AND signal downstream awaits to bail via the abort flag.
  // Idempotent: safe to call when nothing is polling.
  //
  // IMPORTANT: the abort-flag set here is the reason this must NOT be
  // called from inside askForMoreTime / handleNoResponse etc. to reset
  // prior state — use clearMoreTimeCheckState() instead. Setting the
  // abort flag from within the flow itself causes self-inflicted aborts.
  const cancelMoreTimeCheck = () => {
    shouldAbortMoreTimeRef.current = true;
    clearMoreTimeCheckState();
  };
  

  
  useEffect(() => {
    requestQueue.onCheatingDetected(() => {
      setCheatingAlert(true);
      setTimeout(() => setCheatingAlert(false), 3000); // hide after 3 seconds
    });
  }, []);

  // Keep the ref mirror of recognisingText in sync so pollers (setInterval
  // callbacks inside askForMoreTime etc.) can read the latest interim text
  // without capturing a stale React state closure.
  useEffect(() => {
    recognisingTextRef.current = recognisingText || "";
  }, [recognisingText]);

  // Control video upload based on listening/speaking state
  useEffect(() => {
    if (isRecording) {
      requestQueue.resumeScreenUploads();
    } else {
      requestQueue.pauseScreenUploads();
    }
  }, [isRecording]);

  // Audio references - created once and reused
  const audioElementsRef = useRef({
    audio1: null,
    audio2: null,
    audio3: null,
    audio4: null,
    audio5: null,
    audio6: null,
    audio7: null,
    audio8: null
  });

  // Extract test_id from URL
  const searchParams = new URLSearchParams(location.search);
  const test_id = searchParams.get("test_id"); // Extract the test_id value

  // Get initial state from location, with fallback to sessionStorage
  const getInitialState = () => {
    if (location.state && location.state.questions) {
      // Save to sessionStorage for persistence across React Strict Mode remounts
      sessionStorage.setItem(`questionSection_${test_id}`, JSON.stringify(location.state));
      return location.state;
    }
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem(`questionSection_${test_id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved state:", e);
      }
    }
    return {};
  };
  
  const initialState = getInitialState();
  let { questions, resumeQuestions, testType, backendLanguage, isVeryHard, codingQuestions } = initialState;

  useEffect(() => {
    if (testType === "rs") {
      setIsDynamicTest(true);
      setMaxQuestions(15);
    }
  }, [testType]);
  
  const audioChunksRef = useRef([]);
  const silenceStartRef = useRef(null);
  const monitorIntervalRef = useRef(null);

  const analyserRef = useRef(null);
  const sourceRef = useRef(null);

  const SILENCE_THRESHOLD = 0.05; // Kept for legacy references — VAD path uses its own thresholds
  // Silence duration is a ref (mutable across renders) instead of a `let` so
  // that the "more time" bump (5s → 7s) actually persists into subsequent
  // monitor cycles. The old `let SILENCE_DURATION` was reset on every render.
  const silenceMsRef = useRef(5000);
  // SmartSilenceDetector — created lazily once the mic stream is available,
  // then reused across every question. The init promise ref prevents
  // duplicate creation if two monitor calls race.
  const smartDetectorRef = useRef(null);
  const smartDetectorInitRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const startTimeRef = useRef(null);

  // Calculate total questions based on test type
  const totalQuestions = initialState.total_questions || questions.length - 1;

  // Initialize audio elements once
  useEffect(() => {
    const audioFiles = {
      audio1: 'audio1.mp3',
      audio2: 'audio2.mp3',
      audio3: 'audio3.mp3',
      audio4: 'audio4.mp3',
      audio5: 'audio5.mp3',
      audio6: 'audio6.mp3',
      audio7: 'audio7.mp3',
      audio8: 'audio8.mp3',
      audio9: 'audio9.mp3',
      audio10: 'audio10.mp3',
      audio11: 'audio11.mp3',
      audio12: 'audio12.mp3',
      audio13: 'audio13.mp3',
      audio14: 'audio14.mp3',
      audio15: 'audio15.mp3'
    };

    Object.keys(audioFiles).forEach(key => {
      audioElementsRef.current[key] = new Audio(audioFiles[key]);
    });

    return () => {
      // Clean up audio elements
      Object.values(audioElementsRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, []);

  // Check if we're in full-screen mode
  const checkFullScreen = () => {
    const fullscreenElement = document.fullscreenElement || 
                              document.webkitFullscreenElement ||
                              document.mozFullScreenElement ||
                              document.msFullscreenElement;
    setIsFullScreen(!!fullscreenElement);
    
    if (!fullscreenElement && screenSharingActive) {
      handleViolation("fullscreen");
    }
  };

  // Handle various types of violations
  const handleViolation = (type) => {
    // Log to desktop proctoring if running in desktop app
    if (isDesktopApp()) {
      const messages = {
        'fullscreen': 'Exited fullscreen',
        'tab_switch': 'Tab switch detected',
        'window_blur': 'Window lost focus (Alt+Tab)'
      };
      logCheatingEvent(type, 'medium', {
        message: messages[type] || 'Unknown violation',
        timestamp: new Date().toISOString()
      });
    }

    if (type === "fullscreen") {
      const newCount = fullscreenExitCount + 1;
      setFullscreenExitCount(newCount);

      if (newCount >= 3) {
        forceZeroMarksSubmission();
        return;
      }

      setShowFullscreenWarning(true);

      let timer = 30;
      setExitTimer(timer);

      if (exitTimerRef.current) {
        clearInterval(exitTimerRef.current);
      }

      exitTimerRef.current = setInterval(() => {
        timer--;
        setExitTimer(timer);

        if (timer <= 0) {
          clearInterval(exitTimerRef.current);
          handleStopInterview();
        }
      }, 1000);
    } else if (type === "tab_switch") {
      const newCount = switchCount + 1;
      setSwitchCount(newCount);

      if (newCount >= 3) {
        forceZeroMarksSubmission();
        return;
      }

      setShowTabSwitchWarning(true);
      setTimeout(() => setShowTabSwitchWarning(false), 3000);
    }
  };

  // Force zero marks submission for violations
  const forceZeroMarksSubmission = async () => {
    await speak("This assessment has been concluded due to proctoring policy violations.");

    interviewActiveRef.current = false;

    if (exitTimerRef.current) clearInterval(exitTimerRef.current);

    // Belt-and-suspenders: fire a sendBeacon to the server telling the
    // recovery worker to prioritize this test_id. Survives even if we
    // race window.close() below and the pending merge fetch gets killed.
    // The recovery worker will still pick this test up on its next tick.
    try {
      if (test_id && navigator.sendBeacon) {
        const form = new FormData();
        form.append('test_id', String(test_id));
        navigator.sendBeacon('/api/trigger_merge_async', form);
      }
    } catch (e) {
      console.warn('[forceZeroMarks] sendBeacon failed:', e);
    }

    // stopScreenRecording() queues the merge on the request queue and
    // returns immediately. Await it AND then explicitly wait for the
    // request queue to drain so the merge fetch actually completes
    // before we close the window.
    await stopScreenRecording();
    try {
      // Wait for all queued requests (including the merge) to finish.
      // Bounded to 20s so a hung request can't stall us indefinitely.
      await Promise.race([
        requestQueue.drainAll(),
        new Promise(res => setTimeout(res, 20000)),
      ]);
    } catch (e) {
      console.warn('[forceZeroMarks] drainAll failed:', e);
    }

    // Stop desktop proctoring and submit data
    if (isDesktopApp()) {
      console.log("[Desktop] Force stopping proctoring due to violations...");
      try {
        const proctoringResult = await stopDesktopProctoring();
        console.log("[Desktop] Proctoring stopped:", proctoringResult);
        await submitProctoringData(test_id, testType);

        // Upload screen recording to S3
        console.log("[Desktop] Uploading screen recording...");
        const uploadResult = await uploadRecording(test_id);
        console.log("[Desktop] Recording upload result:", uploadResult);
      } catch (error) {
        console.error("[Desktop] Error stopping proctoring:", error);
      }
    }

    // Upload session audio before scoring
    await stopSessionRecordingAndUpload();

    // AWAIT the score save. Previously this was fire-and-forget which
    // meant the window.close() below could kill the request in flight.
    try {
      await requestQueue.queueHighPriorityRequest(() =>
        fetch(`/api/save_score_in_bg?test_id=${test_id}`, { method: "POST" })
      );
    } catch (e) {
      console.warn('[forceZeroMarks] save_score_in_bg failed:', e);
    }

    // At this point the merge, audio upload, and score save have all
    // either completed or exhausted their bounded wait. If any still
    // hasn't landed on S3, the server-side recovery worker will pick
    // it up within RECOVERY_INTERVAL_MINUTES (default 10 min). So we
    // can close the window safely — no data loss.
    setTimeout(() => {
      window.opener = null;
      window.close();
    }, 500);
  };

  // Monitor visibility changes (tab switching) and window focus (desktop app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsTabActive(false);
        handleViolation("tab_switch");
      } else {
        setIsTabActive(true);
      }
    };

    // For desktop apps, use window blur/focus events (visibilitychange doesn't fire on Alt+Tab)
    const handleWindowBlur = () => {
      if (isDesktopApp()) {
        setIsTabActive(false);
        handleViolation("window_blur");
      }
    };

    const handleWindowFocus = () => {
      if (isDesktopApp()) {
        setIsTabActive(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [switchCount, fullscreenExitCount]);

  // Monitor full-screen changes
  useEffect(() => {
    const fullscreenChangeEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];
    
    fullscreenChangeEvents.forEach(event => {
      document.addEventListener(event, checkFullScreen);
    });
    
    return () => {
      fullscreenChangeEvents.forEach(event => {
        document.removeEventListener(event, checkFullScreen);
      });
    };
  }, [screenSharingActive]);

  // Return to fullscreen after warning
  const handleReturnToFullscreen = () => {
    setShowFullscreenWarning(false);
    
    // Check if already in fullscreen
    const isFullscreen = document.fullscreenElement || 
                         document.webkitFullscreenElement || 
                         document.mozFullScreenElement || 
                         document.msFullscreenElement;
    
    if (!isFullscreen) {
      const elem = document.documentElement;
      try {
        if (elem.requestFullscreen) elem.requestFullscreen().catch(() => {});
        else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
        else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
        else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
      } catch (e) {
        // Fullscreen request failed - likely needs user gesture
        console.warn("Fullscreen request failed:", e);
      }
    }
    
    if (exitTimerRef.current) {
      clearInterval(exitTimerRef.current);
    }
  };

  // Function to start screen recording
  const startScreenRecording = async () => {
    try {
      playAudioAndWait("audio9");
      // speak("For this assessment, we need to record your screen. Please select entire screen option from the pop up.");

      // Request screen capture (may or may not include system audio)
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: "screen" },
        audio: true // request system audio if available
      });
      ensureEntireScreenSelected(screenStream);

      // Always request mic separately (user microphone)
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Combine screen video tracks with microphone audio tracks
      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...micStream.getAudioTracks()
      ]);

      // Save refs
      screenStreamRef.current = combinedStream;
      micStreamRef.current = micStream;

      // Use combinedStream (not screenStream) so audio is recorded
      const recorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp9,opus', // include audio codec
        videoBitsPerSecond: 1200000,
        audioBitsPerSecond: 128000
      });

      screenRecorderRef.current = recorder;
      screenChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          screenChunksRef.current.push(event.data);

          // Send chunk to backend every ~10 seconds (you can tune this)
          sendScreenChunkToBackend();
        }
      };

      recorder.onstop = async () => {
        // Ensure any remaining pieces are sent
        if (screenChunksRef.current.length > 0) {
          await sendScreenChunkToBackend();
        }
      };

      // Start recording; timeslice 1000ms (1s) collects data regularly
      recorder.start(10000);
      setIsScreenRecording(true);
      setScreenSharingActive(true);
      setScreenShareError("");
      handleReturnToFullscreen();
      setTestStarted(true);

      // If user stops the screen share from browser UI -> stop our recording
      const vtrack = screenStream.getVideoTracks()[0];
      if (vtrack) {
        vtrack.onended = () => {
          // stop the recorder (if still running)
          if (screenRecorderRef.current && screenRecorderRef.current.state === 'recording') {
            screenRecorderRef.current.stop();
          }
          stopScreenRecording();
        };
      }

    } catch (error) {
      console.error("Error starting screen recording:", error);
      setScreenShareError(error.code === "ENTIRE_SCREEN_REQUIRED"
        ? ENTIRE_SCREEN_REQUIRED_MESSAGE
        : "Screen sharing permission was not granted. Please allow screen sharing to continue with the assessment.");
    }
  };

  // Function to send screen chunk to backend
  const sendScreenChunkToBackend = () => {
    if (screenChunksRef.current.length === 0) return;
    
    try {
      const blob = new Blob(screenChunksRef.current, { type: 'video/webm' });
      const formData = new FormData();
      formData.append('screen_chunk', blob);
      formData.append('test_id', test_id);
      formData.append('chunk_index', Date.now());
      
      // Use the priority queue instead of direct axios call
      // requestQueue.queueScreenChunk(() => 
      //   axios.post('/api/upload_screen_chunk', formData, {
      //     headers: { 'Content-Type': 'multipart/form-data' }
      //   })
      // );
      
      // Clear sent chunks
      screenChunksRef.current = [];
    } catch (error) {
      console.error("Error sending screen chunk to backend:", error);
    }
  };

  // Function to stop screen recording
  const stopScreenRecording = async () => {
    if (screenRecorderRef.current && screenRecorderRef.current.state === 'recording') {
      screenRecorderRef.current.stop();
      
      // Send any remaining chunks
      // if (screenChunksRef.current.length > 0) {
      //   await sendScreenChunkToBackend();
      // }
      
      // Stop all tracks
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }

      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      setIsScreenRecording(false);

      requestQueue.queueHighPriorityRequest(() =>
        fetch('/api/merge_screen_recording', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ test_id })
        })
      );
    }
  };

  const playInstructionAudio = async () => {
    // await speak("Welcome to your A I interview. You will have 1 minute and 30 seconds to answer each question. If there is 2 seconds of silence, the system will automatically move to the next question. Please stay on this page, do not close the tab, and remain in full-screen mode until the interview is complete. Let's begin your interview.");
    await playAudioAndWait("audio10");
  };

  const initializeScreenRecording = async () => {
    setIsButtonDisabled(true);
    try {
      await startScreenRecording();
      await playInstructionAudio();
      handleQuestionSpeech();
    } catch (error) {
      console.error("Failed to initialize screen recording:", error);
    }
  };

  // Wait until the question is loaded
  const waitForQuestionToLoad = async () => {
    return new Promise((resolve) => {
      const checkQuestionLoaded = () => {
        if (question) {
          resolve();
        } else {
          setTimeout(checkQuestionLoaded, 50); // Check again after 50ms
        }
      };
      checkQuestionLoaded();
    });
  };

  const fetchNextQuestion = async () => {
    if (isFetchingNextQuestion) return;
    
    setIsFetchingNextQuestion(true);
    
    try {
      const response = await axios.post('/api/get_next_question', {
        test_id: test_id,
        conversation_history: conversationHistoryRef.current,
        current_question_count: currentQuestionIndexRef.current
      });
      
      if (response.data.success) {
        const newQuestion = response.data.question;
        
        // Add new question to the questions array
        const updatedQuestions = [...questions, { question: newQuestion }];

        questions = updatedQuestions;

        console.log("Fetched new question:", questions);
        
        // Update location state to include new question
        // window.history.replaceState(
        //   { 
        //     ...location.state, 
        //     questions: updatedQuestions 
        //   },
        //   ''
        // );

        conversationHistoryRef.current = [
          ...conversationHistoryRef.current,
          { role: 'assistant', text: newQuestion }
        ];
          
        // Trigger question update
        // setQuestion(newQuestion);
        // currentQuestionRef.current = newQuestion;
        
        return true;
      } else {
        console.error("Failed to fetch next question:", response.data.error);
        return false;
      }
    } catch (error) {
      console.error("Error fetching next question:", error);
      return false;
    } finally {
      setIsFetchingNextQuestion(false);
    }
  };

  const getQuestionText = async () => {
    if ((testType === "t" || testType === "fscp" || testType === "fspb" || testType === "fsc" || testType === "fscc") && [3, 6, 9, 12].includes(currentQuestionIndexRef.current)) {
      technicalQuestionIndexRef.current -= 1;
      if (resumeQuestions && resumeQuestions.length > resumeQuestionsRef.current) {
        const q = resumeQuestions[resumeQuestionsRef.current].question;
        setQuestion(q);
        currentQuestionRef.current = q;
        // persist and tick
        addAssistantMessage(q);
        setConvTick(t => t + 1);

        resumeQuestionsRef.current += 1;
      } else {
        currentQuestionIndexRef.current += 1;
        setDummyState((prev) => prev + 1);
      }
      return;
    } else if (testType === "qt" && [1, 3].includes(currentQuestionIndexRef.current)) {
      technicalQuestionIndexRef.current -= 1;
      if (resumeQuestions && resumeQuestions.length > resumeQuestionsRef.current) {
        const q = resumeQuestions[resumeQuestionsRef.current].question;
        setQuestion(q);
        currentQuestionRef.current = q;

        addAssistantMessage(q);
        setConvTick(t => t + 1);
        resumeQuestionsRef.current += 1;
      } else {
        currentQuestionIndexRef.current += 1;
        setDummyState((prev) => prev + 1);
      }
      return;
    }

    // If personality -> announce transition
    if (testType === "a" && currentQuestionIndexRef.current === 8) {
      await speak("Your personality test is completed. Now, let's begin the behavioral test.");
    }

    try {
      if (technicalQuestionIndexRef.current >= questions.length) {
        // If dynamic test, try to fetch now (await) and then continue.
        if (isDynamicTest && questions.length < 15) {
          const fetched = await fetchNextQuestion();
          if (!fetched) {
            technicalQuestionIndexRef.current += 1;
            setDummyState(prev => prev + 1);
            return;
          }
        } else {
          // No more questions available in the array and not a dynamic test.
          // This happens when the backend advertised total_questions=N but actually generated <N
          // questions (technical + resume). Don't leave the candidate stuck on an idle screen —
          // prompt them to submit so the interview ends cleanly.
          console.warn('[interview] technicalQuestionIndexRef out of range and not dynamic — prompting candidate to submit');
          setReadyToSubmit(true);
          isProcessingNextQuestionRef.current = false;
          try {
            await speak("Your interview is complete. Please click the Submit Interview button to finish.");
          } catch (e) {
            console.warn('[interview] completion prompt speak failed (non-fatal):', e);
          }
          return;
        }
      }

      const question_data = questions[technicalQuestionIndexRef.current]?.question;

      // Check if question_data is invalid
      if (question_data == undefined || question_data == null || question_data === "" || question_data.length === 0) {
        // Safety: if advancing past this would exceed the questions array, no more questions to try.
        // End gracefully via the readyToSubmit path instead of bouncing the useEffect forever.
        if (technicalQuestionIndexRef.current + 1 >= questions.length) {
          console.warn('[interview] No more valid questions in array — prompting candidate to submit');
          setReadyToSubmit(true);
          isProcessingNextQuestionRef.current = false;
          try {
            await speak("Your interview is complete. Please click the Submit Interview button to finish.");
          } catch (e) {
            console.warn('[interview] completion prompt speak failed (non-fatal):', e);
          }
          return;
        }
        technicalQuestionIndexRef.current += 1;
        setDummyState((prev) => prev + 1); // Force re-render to trigger useEffect if any
        return;
      }

      // Set current question
      setQuestion(question_data);
      currentQuestionRef.current = question_data;

      // persist assistant question into conversation and tick
      addAssistantMessage(question_data);
      setConvTick(t => t + 1);

      // Move control indexes: usually you will call this when moving to the next question, so the caller should update indexes.
      // After showing the question, start background prefetch if dynamic test and buffer low
      if (isDynamicTest && questions.length < 15) {
        conversationHistoryRef.current = [
          ...conversationHistoryRef.current,
          { role: 'assistant', text: question_data }
        ];
        fetchNextQuestion().catch(err => console.error('prefetch error', err));
        // if only PREFETCH_BUFFER or fewer remain ahead of technical index, fetch in background
        // const aheadCount = questions.length - (technicalQuestionIndexRef.current + 1);
        // if (aheadCount < 2) {
        //   // do not await - fire and forget
        //   fetchNextQuestion().catch(err => console.error('prefetch error', err));
        // }
      }
    } catch (error) {
      console.error('Error occurred in getQuestionText:', error);
      // In case of error, increase the index to move to the next question
      technicalQuestionIndexRef.current += 1;
      currentQuestionIndexRef.current += 1;
      setDummyState((prev) => prev + 1); // Force re-render
      return;
    }
  };

  const generateAudio = async (sentence) => {
    return new Promise((resolve, reject) => {
      requestQueue.queueHighPriorityRequest(async () => {
        try {
          const response = await axios.post(
            "/api/generate_audio",
            { text: sentence },
            { responseType: "blob", timeout: 10000 } // 10s timeout — prevents TTS hang freezing the interview
          );
          resolve(response);
        } catch (error) {
          // Don't block the interview for a TTS failure — resolve null so speak() falls back to silence
          console.warn('[tts] generateAudio failed (non-fatal):', error?.message || error);
          resolve(null);
        }
      });
    });
  };

  async function speak(text) {
    const sentences = text
      .split(/\. +/)
      .map(sentence => sentence.trim())
      .filter(Boolean)
      .map(sentence => sentence.endsWith('.') ? sentence : sentence + '.');

    if (sentences.length === 0) return;

    // Function to fetch audio for a sentence using your Flask API
    async function fetchAudio(sentence) {
      try {
        const response = await generateAudio(sentence);

        return URL.createObjectURL(response.data);
      } catch (error) {
        return null;
      }
    }

    // Fetch and play the first sentence
    let currentAudioUrl = await fetchAudio(sentences[0]);

    setIsSpeaking(true);
    for (let i = 1; i < sentences.length; i++) {
      // Start fetching next audio concurrently.
      const nextAudioPromise = fetchAudio(sentences[i]);
      if (currentAudioUrl === null) {
        await speak_def(sentences[i - 1]);
      } else {
        try {
          await playAudio(currentAudioUrl);
        } catch (error) {
          console.log(error);
        }
      }

      currentAudioUrl = await nextAudioPromise;
    }

    // Play the final chunk.
    setIsSpeaking(true);
    if (currentAudioUrl === null) {
      await speak_def(sentences[sentences.length - 1])
    } else {
      try {
        await playAudio(currentAudioUrl);
      } catch (error) {
        console.log(error);
      }
    }
    setIsSpeaking(false);
  };

  // Speak Function
  async function speak_def(textToSpeak) {
    if (textToSpeak === null || textToSpeak === "" || textToSpeak == [] || textToSpeak?.length === 0 || textToSpeak === undefined) {
      return
    }

    await EasySpeech.init();

    await EasySpeech.speak({
      text: textToSpeak,
      // voice: voice,
    });

    return
  };

  function playAudio(url) {
    return new Promise((resolve, reject) => {
      const audio = new Audio(url);

      audio.play();
      // Resolve the promise when audio ends
      audio.onended = () => {
        clearInterval(checkInterval);
        resolve();
      };

      // Reject the promise on error
      audio.onerror = (e) => {
        clearInterval(checkInterval);
        reject(e);
      };

      // Periodically check if interviewActive is false
      const checkInterval = setInterval(() => {
        if (!interviewActiveRef.current) {
          audio.pause();  // Pause the audio
        }
      }, 100);  // Check every 100ms

    });
  };

  const streamRef = useRef(null);

  let mediaRecorder;

  async function startStreaming(retryCount = 0) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      // Hand the same webcam stream to the proctoring engines (reuse, not a
      // second camera). Safe if it stays null briefly — the engines no-op.
      setProctorStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      } else if (retryCount < 5) {
        // Ref not ready yet, retry after a short delay
        setTimeout(() => {
          if (videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
          } else {
            startStreaming(retryCount + 1);
          }
        }, 200);
        return;
      }

      mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      mediaRecorder.start(1000); // send every second
    } catch (err) {
      console.error("Error starting stream:", err);
    }
  }

  // Initialize Web Speech API
  // const initializeSpeechRecognition = () => {
  //   const SpeechRecognition =
  //     window.SpeechRecognition || window.webkitSpeechRecognition;

  //   if (!SpeechRecognition) {
  //     alert("Live Speech-to-Text is not supported in this browser.");
  //     return;
  //   }

  //   const recognition = new SpeechRecognition();
  //   recognition.lang = "en-US";
  //   recognition.continuous = true;
  //   recognition.interimResults = true;

  //   recognition.onresult = (event) => {
  //     const transcript = Array.from(event.results)
  //       .map((result) => result[0].transcript)
  //       .join("");
  //     transcriptionRef.current = transcript;
  //   };

  //   recognition.onerror = (event) => {
  //     console.error("Speech recognition error:", event.error);
  //   };

  //   return recognition;
  // }

// Add this function to start live monitoring with timeout
const startLiveMonitoring = () => {
  // Clear any existing interval
  if (liveMonitoringIntervalRef.current) {
    clearInterval(liveMonitoringIntervalRef.current);
  }

  if (nonStopLiveMonitoringIntervalRef.current) {
    clearInterval(nonStopLiveMonitoringIntervalRef.current);
  }
  
  // Check for special requests every second
  liveMonitoringIntervalRef.current = setInterval(async () => {
    // Check for repeat question requests (only if no request has been made yet)
    if (moreTimeRef.current < 3) {
      if (!requestMadeRef.current && isRequestingRepeatQuestion(transcriptionRef.current)) {
        moreTimeRef.current += 1;
        await handleRepeatRequest();
        return;
      }
      
      // Check for more time requests (only if no request has been made yet)
      if (!requestMadeRef.current && isRequestingMoreTime(transcriptionRef.current)) {
        moreTimeRef.current += 1;
        await handleMoreTimeRequest();
        return;
      }
    }
  }, 1000); // Check every second

  nonStopLiveMonitoringIntervalRef.current = setInterval(async () => {
    // Check for Next Question requests
    if (isRequestingNextQuestion(transcriptionRef.current)) {
      await handleNextQuestionRequest();
      return;
    }

  }, 1000);
  
  // Set timeout to stop live monitoring after 30 seconds
  liveMonitoringTimeoutRef.current = setTimeout(() => {
    stopLiveMonitoring();
  }, 30000); // 30 seconds
};

const startNonStopLiveMonitoring = () => {
  // Clear any existing interval
  if (nonStopLiveMonitoringIntervalRef.current) {
    clearInterval(nonStopLiveMonitoringIntervalRef.current);
  }

  nonStopLiveMonitoringIntervalRef.current = setInterval(async () => {
    // Check for Next Question requests
    if (isRequestingNextQuestion(transcriptionRef.current)) {
      await handleNextQuestionRequest();
      return;
    }

  }, 1000);
}

// Modify the stopLiveMonitoring function to clear the timeout as well
const stopLiveMonitoring = () => {
  if (liveMonitoringIntervalRef.current) {
    clearInterval(liveMonitoringIntervalRef.current);
    liveMonitoringIntervalRef.current = null;
  }
  if (liveMonitoringTimeoutRef.current) {
    clearTimeout(liveMonitoringTimeoutRef.current);
    liveMonitoringTimeoutRef.current = null;
  }
};

const stopNonStopLiveMonitoring = () => {
  if (nonStopLiveMonitoringIntervalRef.current) {
    clearInterval(nonStopLiveMonitoringIntervalRef.current);
    nonStopLiveMonitoringIntervalRef.current = null;
  }
};

// Add this function to handle next question requests
const handleNextQuestionRequest = async () => {
  // Check if already processing
  if (isProcessingNextQuestionRef.current) {
    console.log("Already processing next question, ignoring duplicate request");
    return;
  }

  // Set flag immediately to prevent duplicate calls
  // isProcessingNextQuestionRef.current = true;

  // Mark that a request has been made
  requestMadeRef.current = true;

  // Stop live monitoring AND VAD. isProcessingNextQuestionRef normally
  // guards the VAD callback, but it only stays true for 1s (setTimeout
  // reset) — audio13 playback can take 2-3s, opening a window where VAD
  // could fire onSilence during the ack audio. Pause it explicitly.
  stopLiveMonitoring();
  stopNonStopLiveMonitoring();
  stopMonitorAudio();

  // Stop the current recording
  await stopRecordingWithoutHandleNext();
  
  // Acknowledge the request
  await playAudioAndWait("audio13");
  
  // Proceed to next question using existing handler
  handleNextQuestion();
  
  // Reset flag after a delay to allow the next question to start properly
  setTimeout(() => {
    isProcessingNextQuestionRef.current = false;
  }, 1000);
};

// Modify the handleRepeatRequest function
const handleRepeatRequest = async () => {
  if (isProcessingNextQuestionRef.current) {
    return;
  }
  // Set flag immediately
  // isProcessingNextQuestionRef.current = true;

  // Mark that a request has been made for this question
  requestMadeRef.current = true;

  // Stop ALL monitoring — live phrase pollers AND VAD silence detector.
  // If VAD keeps running while we're playing the repeat via speak(), it
  // detects the candidate's (correct) silence, fires onSilence, and
  // because requestMadeRef is already true it falls to the "move to next
  // question" branch. handleNextQuestion advances mid-repeat, which
  // triggers the useEffect-on-question to speak the NEXT question over
  // the still-playing repeat audio — two voices at once. (Same reason we
  // pause VAD in askForMoreTime.)
  stopLiveMonitoring();
  stopNonStopLiveMonitoring();
  stopMonitorAudio();

  // Stop the current recording
  await stopRecordingWithoutHandleNext();

  // Repeat the question
  await speak(`Okay, I am repeating the question. ${currentQuestionRef.current}`);
  
  // Restart recording with normal silence duration
  silenceMsRef.current = 5000;
  if (smartDetectorRef.current) smartDetectorRef.current.setSilenceMs(5000);
  await startRecording();

  // Reset flag after recording starts
  setTimeout(() => {
    isProcessingNextQuestionRef.current = false;
  }, 1000);
};

// Modify the handleMoreTimeRequest function
const handleMoreTimeRequest = async () => {
  // Check if already processing
  if (isProcessingNextQuestionRef.current) {
    return;
  }

  // Set flag immediately
  // isProcessingNextQuestionRef.current = true;

  // Mark that a request has been made for this question
  requestMadeRef.current = true;

  // Stop ALL monitoring — same reasoning as handleRepeatRequest: if VAD
  // keeps running while speak() plays the "take your time" message, it
  // fires onSilence on the candidate's (correct) listening silence and
  // advances to next question over the still-playing prompt.
  stopLiveMonitoring();
  stopNonStopLiveMonitoring();
  stopMonitorAudio();

  // Stop the current recording
  await stopRecordingWithoutHandleNext();

  // ── "Yes I need more time" grant ────────────────────────────────────────
  // Three coordinated changes so this actually feels like a meaningful
  // grant instead of the previous +2-second stub:
  //   1. Honest audio message via dynamic TTS (bypasses the pre-recorded
  //      audio6.mp3 which lied about "10 more seconds to answer" — the
  //      old code only bumped silence to 7s, and the ceiling was actually
  //      unlimited-speaking-with-15s-pause-tolerance after this fix).
  //   2. Silence tolerance 5s → 15s so real thinking pauses don't cut
  //      the candidate off. Safe because VAD only counts TRUE silence.
  //   3. Per-question timer extended by 60s so the total budget matches
  //      the promise ("more time" means real extra time, not just pause
  //      tolerance).
  try {
    await speak("Take your time. I'll wait patiently. Continue when you're ready.");
  } catch (_) {
    // If dynamic TTS fails for any reason, fall back to the pre-recorded
    // clip so the flow never stalls silently.
    try { await playAudioAndWait("audio6"); } catch (_) {}
  }

  silenceMsRef.current = 15000;
  if (smartDetectorRef.current) smartDetectorRef.current.setSilenceMs(15000);
  setTimeLeft(prev => prev + 60);
  await startRecording();

  // Reset flag after recording starts
  setTimeout(() => {
    isProcessingNextQuestionRef.current = false;
  }, 1000);
};

// Helper to cleanup audio context and nodes
const cleanupAudioContext = (force = false) => {
  console.log('[audio] cleanupAudioContext called, force:', force, 'globalAudioLock:', globalAudioLock);

  // If not forcing and lock is held, skip cleanup (Strict Mode protection)
  if (!force && globalAudioLock) {
    console.log('[audio] Skipping cleanup - lock is held');
    return;
  }

  // Tear down the VAD detector — its ONNX worklet holds the same MediaStream
  // that's about to be released here, and needs to be stopped before the
  // stream tracks are dropped or it will log noisy errors.
  if (smartDetectorRef.current) {
    try { smartDetectorRef.current.stop(); } catch (e) { /* swallow */ }
    smartDetectorRef.current = null;
    smartDetectorInitRef.current = null;
  }
  // Release the module-level mic stream reference so a subsequent
  // startRecording opens a fresh one.
  globalMicStream = null;

  // Clear refs first to prevent any race conditions
  const source = sourceRef.current || globalSourceNode;
  const analyser = analyserRef.current || globalAnalyserNode;
  const audioContext = audioContextRef.current || globalAudioContext;

  sourceRef.current = null;
  analyserRef.current = null;
  audioContextRef.current = null;
  globalSourceNode = null;
  globalAnalyserNode = null;
  globalAudioContext = null;
  
  try {
    if (source) {
      try {
        source.disconnect();
        console.log('[audio] Source disconnected');
      } catch (e) {
        console.log('[audio] Source disconnect error (expected):', e.message);
      }
    }
    if (analyser) {
      try {
        analyser.disconnect();
        console.log('[audio] Analyser disconnected');
      } catch (e) {
        console.log('[audio] Analyser disconnect error (expected):', e.message);
      }
    }
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close().catch(() => {});
      console.log('[audio] AudioContext closed');
    }
  } catch (e) {
    console.log('[audio] Cleanup error (expected):', e.message);
  }
  
  // Release lock
  globalAudioLock = false;
  sessionStorage.removeItem(AUDIO_SESSION_LOCK_KEY);
  console.log('[audio] Cleanup complete, lock released');
};

// Update your startRecording to use the improved function
// ── Session-level recording helpers ─────────────────────────────────────
// startSessionRecording() is called ONCE when the first question begins.
// It keeps one continuous .webm for the full interview so AudioAnalysis
// gets real acoustic data instead of text-based proxies.

// startSessionRecording accepts the already-obtained mic stream so we
// don't race with a second getUserMedia call inside startRecording.
const startSessionRecording = (stream) => {
  if (sessionRecorderRef.current) {
    console.log('[session/audio] Already recording — skipping');
    return;
  }
  try {
    if (typeof MediaRecorder === 'undefined') {
      console.error('[session/audio] MediaRecorder not supported in this browser');
      return;
    }
    const mimeType = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ].find(m => MediaRecorder.isTypeSupported(m)) || '';
    sessionMimeRef.current = mimeType || 'audio/webm';

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    sessionRecorderRef.current = recorder;
    sessionChunksRef.current   = [];

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        sessionChunksRef.current.push(e.data);
      }
    };
    recorder.onerror = (e) => {
      console.error('[session/audio] MediaRecorder error:', e);
    };
    recorder.start(1000); // 1-second chunks
    console.log('[session/audio] ✅ Full-session recording STARTED, mimeType:', recorder.mimeType, 'test_id:', test_id);
  } catch (err) {
    console.error('[session/audio] ❌ Session recorder start FAILED:', err);
  }
};

const stopSessionRecordingAndUpload = async () => {
  const recorder = sessionRecorderRef.current;
  console.log('[session/audio] stopSessionRecordingAndUpload called, recorder state:', recorder?.state, 'chunks:', sessionChunksRef.current.length);

  if (!recorder || recorder.state === 'inactive') {
    console.warn('[session/audio] No active recorder — nothing to upload');
    return;
  }

  await new Promise((resolve) => {
    recorder.onstop = () => {
      console.log('[session/audio] Recorder stopped, chunks collected:', sessionChunksRef.current.length);
      resolve();
    };
    try { recorder.stop(); } catch (e) { resolve(); }
  });
  sessionRecorderRef.current = null;

  const chunks = sessionChunksRef.current;
  sessionChunksRef.current = [];
  if (!chunks.length) {
    console.error('[session/audio] ❌ No audio chunks captured — upload skipped');
    return;
  }

  console.log('[session/audio] Uploading', chunks.length, 'chunks to /api/participant/audio, test_id:', test_id);
  try {
    const blob = new Blob(chunks, { type: sessionMimeRef.current || 'audio/webm' });
    const form = new FormData();
    form.append('audio',   blob,    'interview.webm');
    form.append('test_id', test_id || '');

    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 30000);

    const resp = await fetch('/api/participant/audio', {
      method:      'POST',
      credentials: 'include',
      body:         form,
      signal:       controller.signal,
    });
    clearTimeout(timeoutId);

    if (resp.ok) {
      const data = await resp.json().catch(() => ({}));
      console.log('[session/audio] ✅ Upload SUCCESS:', data);
    } else {
      const text = await resp.text().catch(() => '');
      console.error('[session/audio] ❌ Upload FAILED — HTTP', resp.status, text);
    }
  } catch (err) {
    console.error('[session/audio] ❌ Upload ERROR (non-fatal):', err.name, err.message);
  }
};

const startRecording = async () => {
  console.log('[audio] startRecording called, globalAudioLock:', globalAudioLock);
  
  // Check for existing lock (React Strict Mode protection)
  const existingLock = sessionStorage.getItem(AUDIO_SESSION_LOCK_KEY);
  const now = Date.now();
  
  // If there's a recent lock (within last 5 seconds), reuse existing audio context
  if (existingLock && (now - parseInt(existingLock, 10)) < 5000) {
    console.log('[audio] Recent lock exists, checking for existing audio context');
    if (globalAudioContext && globalAudioContext.state !== 'closed') {
      console.log('[audio] Reusing existing audio context');
      audioContextRef.current = globalAudioContext;
      sourceRef.current = globalSourceNode;
      analyserRef.current = globalAnalyserNode;
      setIsRecording(true);
      startMonitorAudio();
      await startAzureRecognition();
      return;
    }
  }
  
  // Acquire lock
  globalAudioLock = true;
  sessionStorage.setItem(AUDIO_SESSION_LOCK_KEY, now.toString());
  console.log('[audio] Lock acquired');
  
  setIsRecording(true);
  transcriptionRef.current = "";
  recordingStartTimeRef.current = Date.now();

  // Cleanup any existing audio context first (force cleanup since we have the lock)
  cleanupAudioContext(true);

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    console.log('[audio] Got media stream');
    // Publish the stream so the VAD detector (and any other consumer that
    // needs raw audio) can attach without opening a second mic session.
    globalMicStream = stream;

    // Start session-level recorder on FIRST question only, using the same stream
    startSessionRecording(stream);

    startLiveMonitoring();

    // Create new audio context and store in both ref and global
    const newAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    const newSource = newAudioContext.createMediaStreamSource(stream);
    const newAnalyser = newAudioContext.createAnalyser();
    newAnalyser.fftSize = 1024;
    newSource.connect(newAnalyser);

    // Store in refs
    audioContextRef.current = newAudioContext;
    sourceRef.current = newSource;
    analyserRef.current = newAnalyser;

    // Also store in module-level for Strict Mode persistence
    globalAudioContext = newAudioContext;
    globalSourceNode = newSource;
    globalAnalyserNode = newAnalyser;
    
    console.log('[audio] Audio context created and connected');

    const options = { mimeType: "audio/webm;codecs=opus" };
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        audioChunksRef.current.push(e.data);
      }
    };
    mediaRecorder.onstart = () => {
      startTimeRef.current = new Date();
    };
    mediaRecorder.start(1000);
    mediaRecorderRef.current = mediaRecorder;
    startMonitorAudio();

    // Ensure Azure recognition is stopped before starting
    await stopAzureRecognition();
    await startAzureRecognition();
    
    console.log('[audio] Recording started successfully');
  } catch (err) {
    console.error('[audio] Error in startRecording:', err);
    globalAudioLock = false;
    sessionStorage.removeItem(AUDIO_SESSION_LOCK_KEY);
  }
};

const handleRecordingTimeout = async () => {
  if (recordingMoreTimeoutRef.current) {
    // Already handled a more time request timeout, just stop
    await stopRecording();
    handleNextQuestion();
    return;
  }
  recordingMoreTimeoutRef.current = true;
  // Stop the current recording first
  await stopRecordingWithoutHandleNext();
  
  // Ask if user wants more time
  const wantsMoreTime = await askForMoreTimeAfterTimeout();
  
  if (wantsMoreTime) {
    requestMadeRef.current = true;
    // Give user 1 more minute to wrap up. Announcement says "one minute",
    // UI counts down from 60s, and the recorder now actually keeps
    // recording for 60s (previous code used 10 * 1000 = 10 seconds,
    // which silently cut off candidates mid-answer while the UI still
    // showed 50+ seconds left).
    await playAudioAndWait("audio12"); // "Please finalize your response within the one minute."
    setTimeLeft(60); // Reset timer to 1 minute
    await startRecordingWithTimeout(60 * 1000); // 1 minute (was 10 * 1000 — the "10 seconds vs 60 seconds" bug)
  } else {
    await playAudioAndWait("audio13"); // "Okay, moving to the next question."
    // Proceed to next question or end interview
    handleNextQuestion();
  }
};

const askForMoreTimeAfterTimeout = async () => {
  // Same guard shape as askForMoreTime / handleNoResponse.
  stopLiveMonitoring();
  stopNonStopLiveMonitoring();
  // Pause VAD so the candidate's (correct) silence during audio11 doesn't
  // fire another silence event and race with this prompt.
  stopMonitorAudio();
  // Reset abort flag AFTER teardown — stopMonitorAudio's internal
  // cancelMoreTimeCheck call sets it true.
  shouldAbortMoreTimeRef.current = false;

  await playAudioAndWait("audio11"); // "Your allotted time has ended. Would you like an additional minute to conclude your answer?"
  if (shouldAbortMoreTimeRef.current) return false;

  await startRecordingWithoutMonitorAudio();
  if (shouldAbortMoreTimeRef.current) return false;
  
  return new Promise(async (resolve) => {
    // Same adaptive-poll pattern as askForMoreTime — check interim text
    // AND register with moreTimeCheckIntervalRef so handleNextQuestion can
    // cancel this poll cleanly if the interview advances mid-check.
    // Use clearMoreTimeCheckState (no abort-flag set) so this reset
    // doesn't self-abort the new poll we're about to spin up.
    clearMoreTimeCheckState();
    moreTimeCheckResolverRef.current = resolve;

    const HARD_TIMEOUT_MS = 20000;
    const IDLE_TIMEOUT_MS = 10000;
    const monitoringStartTime = Date.now();
    let lastActivityAt = Date.now();
    let lastSeenLen = 0;

    const checkResponse = setInterval(async () => {
      if (moreTimeCheckIntervalRef.current !== checkResponse) {
        try { clearInterval(checkResponse); } catch (_) {}
        return;
      }

      const now = Date.now();
      const combined = ((transcriptionRef.current || "") + " " + (recognisingTextRef.current || "")).trim();

      if (combined.length > lastSeenLen) {
        lastSeenLen = combined.length;
        lastActivityAt = now;
      }

      const hardExpired = now - monitoringStartTime > HARD_TIMEOUT_MS;
      const idleExpired = now - lastActivityAt > IDLE_TIMEOUT_MS;

      if (hardExpired || idleExpired) {
        clearInterval(checkResponse);
        moreTimeCheckIntervalRef.current = null;
        moreTimeCheckResolverRef.current = null;
        await stopRecordingWithoutHandleNext();
        resolve(false); // Default to no if no response
        return;
      }

      const transcript = combined.toLowerCase();
      const positiveResponses = ["yes", "yeah", "yup", "sure", "okay", "ok", "affirmative", "more time", "one more", "minute"];
      const negativeResponses = ["no", "nope", "nah", "negative", "that's ok", "i'm done"];

      if (positiveResponses.some(phrase => transcript.includes(phrase))) {
        clearInterval(checkResponse);
        moreTimeCheckIntervalRef.current = null;
        moreTimeCheckResolverRef.current = null;
        await stopRecordingWithoutHandleNext();
        resolve(true);
      } else if (negativeResponses.some(phrase => transcript.includes(phrase))) {
        clearInterval(checkResponse);
        moreTimeCheckIntervalRef.current = null;
        moreTimeCheckResolverRef.current = null;
        await stopRecordingWithoutHandleNext();
        resolve(false);
      }
    }, 500);
    moreTimeCheckIntervalRef.current = checkResponse;
  });
};

const startRecordingWithTimeout = async (timeoutMs) => {
  // Similar to startRecording but with custom timeout
  setIsRecording(true);
  transcriptionRef.current = "";

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  globalMicStream = stream;

  startNonStopLiveMonitoring();

  const options = { mimeType: "audio/webm;codecs=opus" };
  const mediaRecorder = new MediaRecorder(stream, options);
  mediaRecorder.ondataavailable = (e) => {
    if (e.data.size > 0) {
      audioChunksRef.current.push(e.data);
    }
  };
  mediaRecorder.start();
  mediaRecorderRef.current = mediaRecorder;

  // Set custom timeout for wrap-up period
  recordingTimeoutRef.current = setTimeout(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      await stopRecording();
      handleNextQuestion();
    }
  }, timeoutMs);

  monitorAudioWithoutMoreTime(); // Start monitoring

  await Promise.race([
    stopAzureRecognition(),
    new Promise(resolve => setTimeout(() => { console.warn('[audio] stopAzureRecognition timed out (withTimeout) — forcing continue'); resolve(); }, 5000))
  ]);

  startAzureRecognition();

  // try {
  //   if (!speechRecognitionRef.current) {
  //     speechRecognitionRef.current = initializeSpeechRecognition();
  //   }
  //   speechRecognitionRef.current.start();
  // } catch (error) {
  //   console.error("Error starting recording:", error);
  // }
};

const getRMS = (buffer) => {
  let sumSquares = 0;
  for (let i = 0; i < buffer.length; i++) {
    sumSquares += buffer[i] * buffer[i];
  }
  return Math.sqrt(sumSquares / buffer.length);
};

// ─────────────────────────────────────────────────────────────────────────────
// Smart silence detection (Silero VAD via @ricky0123/vad-web).
//
// Replaces the old RMS-energy timer that treated word-gap silences as
// "candidate finished" and cut candidates off mid-sentence — most painfully,
// after the system asked "do you need more time?" and the candidate's own
// reply crossed the 5-second window.
//
// The detector is created lazily the first time a monitor function runs
// after startRecording opened the mic. It is reused across every question
// (via pause/resume) so the ONNX model loads exactly once per interview.
// If VAD fails to initialise, the detector transparently falls back to the
// original RMS threshold so nothing gets bricked.
// ─────────────────────────────────────────────────────────────────────────────

const ensureSmartDetector = async () => {
  if (smartDetectorRef.current) return smartDetectorRef.current;
  if (smartDetectorInitRef.current) return smartDetectorInitRef.current;

  smartDetectorInitRef.current = (async () => {
    // The stream is captured on getUserMedia inside startRecording /
    // startRecordingWithoutMonitorAudio / startRecordingWithTimeout and
    // stored on globalMicStream. Without it we can't attach VAD.
    const stream = globalMicStream;
    if (!stream) {
      console.warn('[vad] ensureSmartDetector: no globalMicStream yet — will retry on next call');
      smartDetectorInitRef.current = null;
      return null;
    }

    const detector = new SmartSilenceDetector({
      stream,
      analyser: analyserRef.current, // used only if VAD load fails
      silenceMs: silenceMsRef.current,
      minSpeechMs: 500,
      onSilence: () => {}, // replaced per-monitor by activateSmartMonitor
      onReady: (mode) => console.log('[vad] detector ready in', mode, 'mode'),
      onFallback: (err) => console.warn('[vad] falling back to RMS timer:', err?.message || err),
    });

    try {
      await detector.start();
      smartDetectorRef.current = detector;
      return detector;
    } catch (err) {
      console.error('[vad] detector.start() failed:', err);
      return null;
    } finally {
      smartDetectorInitRef.current = null;
    }
  })();

  return smartDetectorInitRef.current;
};

// Called by each monitor function. Wires up the callback and (re)arms the
// detector. Idempotent — reuses the same detector across question changes.
const activateSmartMonitor = (onSilence) => {
  monitoringActiveRef.current = true;
  silenceStartRef.current = null; // legacy — kept in sync so external readers see fresh state

  const wrappedCallback = async () => {
    if (!monitoringActiveRef.current || isProcessingNextQuestionRef.current) return;
    try {
      await onSilence();
    } catch (err) {
      console.error('[vad] onSilence callback threw:', err);
    }
  };

  // Fire-and-forget: don't block startRecording on VAD model loading.
  // If the detector isn't ready yet, this promise resolves once it is.
  ensureSmartDetector().then((detector) => {
    if (!detector || !monitoringActiveRef.current) return;
    detector.setSilenceMs(silenceMsRef.current);
    detector.setOnSilence(wrappedCallback);
    detector.resetSpeechState();
    detector.resume();
  }).catch((err) => {
    console.error('[vad] activateSmartMonitor init failed:', err);
  });
};

// Public entry point used by startRecording — matches original semantics:
// on silence, ask for more time (once per question) if within 30s, else
// advance to the next question.
const startMonitorAudio = () => {
  activateSmartMonitor(async () => {
    if (isProcessingNextQuestionRef.current || !monitoringActiveRef.current) return;

    const currentRecordingDuration = Date.now() - recordingStartTimeRef.current;

    if (!requestMadeRef.current && currentRecordingDuration < 30000) {
      requestMadeRef.current = true;
      await stopRecordingWithoutHandleNext();

      const needsMoreTime = await askForMoreTime();

      // Cancellation guard: if cancelMoreTimeCheck ran during askForMoreTime
      // (e.g. because handleNextQuestion was triggered by nonStopLiveMonitoring
      // catching a stray "next question" phrase), the interview has already
      // advanced. Do NOT call handleNextQuestion again — the flow is done.
      if (shouldAbortMoreTimeRef.current) {
        console.log("[more-time] cancelled mid-flow — skipping advance (already advanced)");
        return;
      }

      if (needsMoreTime) {
        // See handleMoreTimeRequest for the reasoning behind these three
        // coordinated values — honest TTS message, 15s silence tolerance,
        // +60s on the per-question timer. Keep in sync with that call
        // site or the two paths will diverge in candidate experience.
        try {
          await speak("Take your time. I'll wait patiently. Continue when you're ready.");
        } catch (_) {
          try { await playAudioAndWait("audio6"); } catch (_) {}
        }
        silenceMsRef.current = 15000;
        if (smartDetectorRef.current) smartDetectorRef.current.setSilenceMs(15000);
        setTimeLeft(prev => prev + 60);
        await startRecording();
        retryAttemptsRef.current = 1;
        return;
      }

      await stopRecording();
      handleNextQuestion();
      setTimeout(() => {}, 1000);
    } else {
      console.log("Silence detected, moving to next question.");
      await stopRecording();
      handleNextQuestion();
      setTimeout(() => {
        isProcessingNextQuestionRef.current = false;
      }, 1000);
    }
  });
};

// Historical alias — some code paths call monitorAudio() directly. Same
// behaviour as startMonitorAudio in the original implementation.
const monitorAudio = () => startMonitorAudio();

// Used after "more time" has already been granted: no additional prompt,
// silence goes straight to the next question.
const monitorAudioWithoutMoreTime = () => {
  activateSmartMonitor(async () => {
    if (!monitoringActiveRef.current) return;
    await stopRecording();
    handleNextQuestion();
  });
};

const stopMonitorAudio = () => {
  monitoringActiveRef.current = false;
  if (smartDetectorRef.current) {
    try { smartDetectorRef.current.pause(); } catch (e) { /* swallow */ }
  }
  // Legacy fallback — if some code path still holds an old setInterval,
  // clear it too.
  if (monitorIntervalRef.current) {
    clearInterval(monitorIntervalRef.current);
    monitorIntervalRef.current = null;
  }
  silenceStartRef.current = null;

  // Clear any pending "more time" / "are you there?" poll (interval,
  // resolver, audio) — but do NOT set the abort flag. Callers of
  // stopMonitorAudio like askForMoreTime need to reuse this to pause VAD
  // as part of their normal setup; if we set the abort flag here, the
  // poll they're about to start would abort itself. The abort flag is
  // set separately by cancelMoreTimeCheck from handleNextQuestion.
  clearMoreTimeCheckState();
};

// Update stopRecording to check the flag
const stopRecording = async () => {
  console.log(`stopRecording called. isProcessingNextQuestion: ${isProcessingNextQuestionRef.current}`);
  // Check if already processing
  if (isProcessingNextQuestionRef.current) {
    console.log("Already processing next question in stopRecording");
    return;
  }

  isProcessingNextQuestionRef.current = true;

  setIsRecording(false);

  // Wrap Azure stop in a 5s timeout — if it hangs the flag stays true forever and freezes the interview
  await Promise.race([
    stopAzureRecognition(),
    new Promise(resolve => setTimeout(() => { console.warn('[audio] stopAzureRecognition timed out — forcing continue'); resolve(); }, 5000))
  ]);

  const durationMs = Date.now() - recordingStartTimeRef.current;
  recordingDurationRef.current = durationMs / 1000;

  const currentTranscription = transcriptionRef.current;
  const capturedQuestionIndex = currentQuestionIndexRef.current;
  
  uploadAudioInChunks(capturedQuestionIndex, currentTranscription);

  // handleNextQuestion();

  if (recordingTimeoutRef.current) {
    clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = null;
  }

  if (monitorIntervalRef.current) {
    clearInterval(monitorIntervalRef.current);
  }

  if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
    mediaRecorderRef.current.stop();

    mediaRecorderRef.current.onstop = async () => {
      

      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      audioRef.current = audioBlob;

      audioChunksRef.current = [];
      URL.revokeObjectURL(audioRef.current);
    };
  }

  setTimeout(() => {
    isProcessingNextQuestionRef.current = false;
  }, 1000);
};

// Update stopRecordingWithoutHandleNext to properly cleanup
const stopRecordingWithoutHandleNext = async () => {
  console.log(`stopRecording without handle next called. isProcessingNextQuestion: ${isProcessingNextQuestionRef.current}`);
  // Check if already processing
  if (isProcessingNextQuestionRef.current) {
    console.log("Already processing next question in stopRecording");
    return;
  }

  isProcessingNextQuestionRef.current = true;

  setIsRecording(false);
  const capturedQuestionIndex = currentQuestionIndexRef.current;

  if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
    mediaRecorderRef.current.stop();
  }

  // Wrap Azure stop in a 5s timeout — prevents hang from keeping isProcessingNextQuestionRef stuck
  await Promise.race([
    stopAzureRecognition(),
    new Promise(resolve => setTimeout(() => { console.warn('[audio] stopAzureRecognition timed out (withoutHandleNext) — forcing continue'); resolve(); }, 5000))
  ]);

  const currentTranscription = transcriptionRef.current;
  uploadAudioInChunks(capturedQuestionIndex, currentTranscription);

  if (recordingTimeoutRef.current) {
    clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = null;
  }

  if (monitorIntervalRef.current) {
    clearInterval(monitorIntervalRef.current);
  }

  stopLiveMonitoring();
  stopNonStopLiveMonitoring();
  
  // Note: Don't cleanup audio context here - it will be reused or cleaned up on next startRecording
  // This prevents issues with React Strict Mode remounts

  if (mediaRecorderRef.current) {
    mediaRecorderRef.current.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      audioRef.current = audioBlob;

      audioChunksRef.current = [];
    };
  }

  transcriptionRef.current = '';

  setTimeout(() => {
    isProcessingNextQuestionRef.current = false;
  }, 1000);
};

const startRecordingWithoutMonitorAudio = async () => {
  console.log('[audio] startRecordingWithoutMonitorAudio called, globalAudioLock:', globalAudioLock);
  
  // Check for existing lock (React Strict Mode protection)
  const existingLock = sessionStorage.getItem(AUDIO_SESSION_LOCK_KEY);
  const now = Date.now();
  
  // If there's a recent lock, reuse existing audio context
  if (existingLock && (now - parseInt(existingLock, 10)) < 5000) {
    if (globalAudioContext && globalAudioContext.state !== 'closed') {
      console.log('[audio] Reusing existing audio context (without monitor)');
      audioContextRef.current = globalAudioContext;
      sourceRef.current = globalSourceNode;
      analyserRef.current = globalAnalyserNode;
      setIsRecording(true);
      await startAzureRecognition();
      return;
    }
  }
  
  // Acquire lock
  globalAudioLock = true;
  sessionStorage.setItem(AUDIO_SESSION_LOCK_KEY, now.toString());
  
  setIsRecording(true);
  recordingStartTimeRef.current = Date.now();

  // Cleanup any existing audio context first (force cleanup)
  cleanupAudioContext(true);

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    globalMicStream = stream;

    // Setup audio context and analyser
    const newAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    const newSource = newAudioContext.createMediaStreamSource(stream);
    const newAnalyser = newAudioContext.createAnalyser();
    newAnalyser.fftSize = 1024;
    newSource.connect(newAnalyser);
    
    // Store in refs and globals
    audioContextRef.current = newAudioContext;
    sourceRef.current = newSource;
    analyserRef.current = newAnalyser;
    globalAudioContext = newAudioContext;
    globalSourceNode = newSource;
    globalAnalyserNode = newAnalyser;

    const options = { mimeType: "audio/webm;codecs=opus" };
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        audioChunksRef.current.push(e.data);
      }
    };
    mediaRecorder.onstart = () => {
      startTimeRef.current = new Date();
    };
    mediaRecorder.start(1000);
    mediaRecorderRef.current = mediaRecorder;

    // Ensure Azure recognition is stopped before starting
    await stopAzureRecognition();
    await new Promise(resolve => setTimeout(resolve, 300));
    await startAzureRecognition();
    
    console.log('[audio] Recording started (without monitor)');
  } catch (err) {
    console.error('[audio] Error in startRecordingWithoutMonitorAudio:', err);
    globalAudioLock = false;
    sessionStorage.removeItem(AUDIO_SESSION_LOCK_KEY);
  }

  // try {
  //   if (!speechRecognitionRef.current) {
  //     speechRecognitionRef.current = initializeSpeechRecognition();
  //   }
  //   speechRecognitionRef.current.start(); // Start live transcription
  //   // await recorderRef.current.start();
  //   // setIsRecording(true);
  // } catch (error) {
  //   console.error("Error starting recording:", error);
  // }
};

const recordFor5SecondsAndTranscribe = async () => {
  return new Promise(async (resolve) => {
    // Start recording
    await startRecordingWithoutMonitorAudio();

    // Wait 5 seconds
    setTimeout(async () => {
      await stopRecordingWithoutHandleNext();

      const startTime = Date.now()

      // Wait until the transcription is done (poll transcriptionRef)
      const checkTranscription = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        if (transcriptionRef.current !== "") {
          clearInterval(checkTranscription);
          resolve(transcriptionRef.current.toLowerCase());
        } else if (elapsedTime >= 10000) {
          // If 10s passed, stop waiting
          clearInterval(checkTranscription);
          resolve(""); // proceed with empty transcription
        }
      }, 500);
    }, 5000);
  });
};

  // Handle Speaking Question
  const handleQuestionSpeech = async () => {
    await waitForQuestionToLoad();
    currentQuestionRef.current = question;

    // Store the question speech in a way that can be resumed
    const speakQuestion = async () => {
      await speak(question); // Speak the question
      if (!interviewActiveRef.current) return;
      transcriptionRef.current = ""; // Reset the transcription state

      // ── No thinking-time pause ──────────────────────────────────────────
      // The old code held a 12-second countdown here before recording
      // started. That was a workaround for the pre-VAD silence detector
      // which would count silence from second 0 — a candidate who paused
      // to think for 5s got treated as "done answering" before they had
      // spoken. VAD makes this hack redundant:
      //
      //   Rule 2 in smartSilenceDetector.js — the silence timer only
      //   starts AFTER the candidate has spoken at least `minSpeechMs`
      //   (500 ms). Zero speech ⇒ silence timer never arms ⇒ system
      //   waits patiently.
      //
      // So we start recording immediately after Sana finishes. The
      // candidate can begin speaking whenever they're ready; VAD holds
      // off silence detection until they actually do. The countdown badge
      // that used to appear on the candidate video is also removed
      // (see JSX — thinkingSeconds > 0 branch was killed).

      // Reset silence threshold to the default at the start of every new
      // question. If the previous question bumped it to 7000 (more-time
      // path) we want to go back to the tighter 5000ms baseline here.
      silenceMsRef.current = 5000;
      if (smartDetectorRef.current) smartDetectorRef.current.setSilenceMs(5000);
      await startRecording();
    };

    await speakQuestion();
  };

  // const transcribeAudio = async (formData) => {
  //   return new Promise((resolve, reject) => {
  //     requestQueue.queueHighPriorityRequest(async () => {
  //       try {
  //         const response = await fetch("/api/upload_chunk", {
  //           method: "POST",
  //           body: formData,
  //         });
  //         resolve(response);
  //       } catch (error) {
  //         reject(error);
  //       }
  //     });
  // })};

  
  // Upload Audio in Chunks or Full for short audio
  async function uploadAudioInChunks(questionIndex, transcription) {
    const formData = new FormData();
    formData.append('question_index', questionIndex);
    formData.append('test_id', test_id);
    formData.append('transcription', transcription);

    conversationHistoryRef.current = [
      ...conversationHistoryRef.current,
      { role: 'user', text: transcription }
    ];

    await fetch('/api/upload_chunk', {
      method: 'POST',
      body: formData,
    });
    // const chunkSize = 1 * 1024 * 1024; // 1 MB
    // const totalChunks = Math.ceil(audioData.size / chunkSize);

    // for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    //     const start = chunkIndex * chunkSize;
    //     const end = Math.min(start + chunkSize, audioData.size);
    //     const audioChunk = audioData.slice(start, end);

        
    //     formData.append('audio_chunk', audioChunk, `chunk_${chunkIndex}.wav`);
    //     formData.append('chunk_index', chunkIndex);
    //     formData.append('total_chunks', totalChunks);
        

    //     // ✅ Only send transcription with the LAST chunk
    //     if (chunkIndex === totalChunks - 1) {
          
    //   }

        
    // }
    // cheatingDetection(questionIndex);
  }

  // Helper function to get audio duration
  // async function getAudioDuration(audioBlob) {
  //   return new Promise((resolve) => {
  //     const audio = new Audio();
  //     audio.src = URL.createObjectURL(audioBlob);
  //     audio.addEventListener('loadedmetadata', () => {
  //       resolve(audio.duration * 1000); // Convert to milliseconds
  //       URL.revokeObjectURL(audio.src);
  //     });
  //     audio.addEventListener('error', () => {
  //       resolve(Infinity); // If can't determine duration, assume it's long
  //     });
  //   });
  // }

  async function playAudioAndWait(audioKey) {
    setIsSpeaking(true);
    try {
        const audio = audioElementsRef.current[audioKey];
        if (!audio) {
          console.warn('[playAudioAndWait] audio element not found for key:', audioKey);
          return;
        }
        await audio.play();
        // Wait for playback to reach a natural end OR be cancelled externally
        // (cancelMoreTimeCheck calls audio.pause() to abort mid-word). The
        // "pause" listener is critical: without it, cancelling a "more time"
        // audio only stops the sound but the awaiter hangs 15s waiting for
        // the "ended" event that will never fire. Also race a 15s hard
        // timeout so a stuck/broken audio clip never freezes the interview.
        await Promise.race([
          new Promise(resolve => audio.addEventListener("ended", resolve, { once: true })),
          new Promise(resolve => audio.addEventListener("pause", resolve, { once: true })),
          new Promise(resolve => setTimeout(() => {
            console.warn('[playAudioAndWait] timeout waiting for audio to end:', audioKey);
            resolve();
          }, 15000))
        ]);
    } catch (error) {
        console.error("Error playing the audio:", error);
    } finally {
        setIsSpeaking(false);
    }
  }

  const askForMoreTime = async () => {
    if (retryAttemptsRef.current > 0 || moreAreYouThereRef.current >= 3) {
      // await playAudioAndWait(audios.audio1);
      return;
    }
    // ── Stop the parallel phrase-detector polls ────────────────────────────
    // Without this, nonStopLiveMonitoring keeps scanning the transcript
    // every second for phrases like "next one" / "move on" / "proceed"
    // WHILE audio2 is playing. Any STT hiccup (echo of Sana's voice
    // through speakers, background noise, similar-sounding word) can
    // trigger handleNextQuestionRequest — which advances to Q(n+1) while
    // askForMoreTime is still expecting a yes/no reply. Result: Q3 audio
    // plays over the "do you need more time?" prompt. Matches the
    // pattern askForMoreTimeAfterTimeout already uses.
    stopLiveMonitoring();
    stopNonStopLiveMonitoring();

    // ── Pause the VAD silence detector ─────────────────────────────────────
    // CRITICAL: the candidate is EXPECTED to be silent while Sana asks
    // "Do you need more time?". If VAD keeps polling, it detects that
    // (correct) silence and fires its onSilence callback again — which
    // falls through to the "move to next question" branch because
    // requestMadeRef is already true. Net effect: audio2 plays over Q(n+1)
    // audio and the candidate is asked to answer both prompts. Pausing
    // stops silence counting; we resume in the "no more time" branch
    // after this Promise settles (see startMonitorAudio callback).
    stopMonitorAudio();

    // Reset abort flag AFTER teardown. stopMonitorAudio internally calls
    // cancelMoreTimeCheck() (defense-in-depth for the handleNextQuestion
    // path), which sets shouldAbortMoreTimeRef = true. Resetting here
    // AFTER that runs is critical — otherwise the very next abort-check
    // fires and we return false immediately, leaving audio2 playing but
    // no poll listening for the candidate's yes/no reply.
    shouldAbortMoreTimeRef.current = false;

    await playAudioAndWait("audio2");
    if (shouldAbortMoreTimeRef.current) return false;
    // await speak("Would you like more time to answer this question Please say yes if you need more time");

    await startRecordingWithoutMonitorAudio();
    if (shouldAbortMoreTimeRef.current) return false;
    return new Promise(async (resolve) => {
      // ── Adaptive poll loop ──────────────────────────────────────────────
      // Check BOTH final transcript AND live interim text — a candidate
      // mid-sentence must not look silent while Azure buffers.
      //
      // Cancellation: this poll registers with moreTimeCheckIntervalRef /
      // moreTimeCheckResolverRef. If handleNextQuestion runs while the
      // poll is still active, it calls cancelMoreTimeCheck() which clears
      // the interval AND resolves this promise as `false`. Without this
      // guard, a stale "Are you there?" would play over the next question.
      // Use clearMoreTimeCheckState (no abort-flag set) so this reset
      // doesn't self-abort the new poll we're about to spin up.
      clearMoreTimeCheckState();
      moreTimeCheckResolverRef.current = resolve;

      const HARD_TIMEOUT_MS = 20000;
      const IDLE_TIMEOUT_MS = 10000;
      const monitoringStartTime = Date.now();
      let lastActivityAt = Date.now();
      let lastSeenLen = 0;

      const checkResponse = setInterval(async () => {
        // Someone (handleNextQuestion) cancelled us — bail silently.
        // The Promise was already resolved by cancelMoreTimeCheck.
        if (moreTimeCheckIntervalRef.current !== checkResponse) {
          try { clearInterval(checkResponse); } catch (_) {}
          return;
        }

        const now = Date.now();
        const combined = ((transcriptionRef.current || "") + " " + (recognisingTextRef.current || "")).trim();

        if (combined.length > lastSeenLen) {
          lastSeenLen = combined.length;
          lastActivityAt = now;
        }

        const hardExpired = now - monitoringStartTime > HARD_TIMEOUT_MS;
        const idleExpired = now - lastActivityAt > IDLE_TIMEOUT_MS;

        if (hardExpired || idleExpired) {
          clearInterval(checkResponse);
          moreTimeCheckIntervalRef.current = null;
          moreTimeCheckResolverRef.current = null;
          if (combined.length === 0) {
            await stopRecordingWithoutHandleNext();
            const result = await handleNoResponse();
            resolve(result);
          } else {
            await stopRecordingWithoutHandleNext();
            resolve(false);
          }
          return;
        }

        const transcript = combined.toLowerCase();
        const positiveResponses = ["yes", "yeah", "yup", "sure", "okay", "ok", "affirmative", "more time", "one more", "give me"];
        const negativeResponses = ["no", "nope", "nah", "negative", "i'm done", "im done"];

        if (positiveResponses.some(phrase => transcript.includes(phrase))) {
          clearInterval(checkResponse);
          moreTimeCheckIntervalRef.current = null;
          moreTimeCheckResolverRef.current = null;
          await stopRecordingWithoutHandleNext();
          resolve(true);
        } else if (negativeResponses.some(phrase => transcript.includes(phrase))) {
          clearInterval(checkResponse);
          moreTimeCheckIntervalRef.current = null;
          moreTimeCheckResolverRef.current = null;
          await stopRecordingWithoutHandleNext();
          await playAudioAndWait("audio3");
          resolve(false);
        }
      }, 500); // Check every 500 ms
      moreTimeCheckIntervalRef.current = checkResponse;
    });
  };

  // Add a helper function for handling no response
  const handleNoResponse = () => {
    moreAreYouThereRef.current += 1;

    return new Promise(async (resolve) => {
      // Same defence as askForMoreTime: kill live-phrase pollers before we
      // start the "Are you there?" prompt. Otherwise a stray "move on"
      // match will advance the interview while this Promise is still
      // pending, and the candidate hears two prompts stacked.
      stopLiveMonitoring();
      stopNonStopLiveMonitoring();
      // Pause VAD too — the candidate is expected to be silent while
      // audio4 ("Are you there?") plays; without pausing, VAD would fire
      // silence again and advance to the next question mid-prompt.
      stopMonitorAudio();
      // Reset abort flag AFTER teardown (stopMonitorAudio calls
      // cancelMoreTimeCheck which sets the flag true).
      shouldAbortMoreTimeRef.current = false;

      await playAudioAndWait("audio4"); // "Are you there?"
      if (shouldAbortMoreTimeRef.current) { resolve(false); return; }

      await startRecordingWithoutMonitorAudio();
      if (shouldAbortMoreTimeRef.current) { resolve(false); return; }

      // Register cancellation. If handleNextQuestion advances the interview
      // while this poll is active, cancelMoreTimeCheck() will resolve `false`
      // and stop this interval — preventing the "Are you there?" prompt
      // from playing over the next question's audio. Reset with
      // clearMoreTimeCheckState (no abort-flag set) so this doesn't
      // self-abort the new poll we're about to spin up.
      clearMoreTimeCheckState();
      moreTimeCheckResolverRef.current = resolve;

      const HARD_TIMEOUT_MS = 15000;
      const IDLE_TIMEOUT_MS = 10000;
      const monitoringStartTime = Date.now();
      let lastActivityAt = Date.now();
      let lastSeenLen = 0;

      const checkResponse = setInterval(async () => {
        if (moreTimeCheckIntervalRef.current !== checkResponse) {
          try { clearInterval(checkResponse); } catch (_) {}
          return;
        }

        const now = Date.now();
        const combined = ((transcriptionRef.current || "") + " " + (recognisingTextRef.current || "")).trim();

        if (combined.length > lastSeenLen) {
          lastSeenLen = combined.length;
          lastActivityAt = now;
        }

        const hardExpired = now - monitoringStartTime > HARD_TIMEOUT_MS;
        const idleExpired = now - lastActivityAt > IDLE_TIMEOUT_MS;

        if (hardExpired || idleExpired) {
          clearInterval(checkResponse);
          moreTimeCheckIntervalRef.current = null;
          moreTimeCheckResolverRef.current = null;
          if (combined.length === 0) {
            await stopRecordingWithoutHandleNext();
            if (moreAreYouThereRef.current < 3) {
              const result = await handleNoResponse();
              resolve(result);
            } else {
              await playAudioAndWait("audio5");
              currentQuestionIndexRef.current = totalQuestions;
              handleStopInterview();
              resolve(false);
            }
          } else {
            await stopRecordingWithoutHandleNext();
            const result = await askForMoreTime();
            resolve(result);
          }
          return;
        }

        // Any speech at all → they're there, escalate to askForMoreTime.
        if (combined.length > 0) {
          clearInterval(checkResponse);
          moreTimeCheckIntervalRef.current = null;
          moreTimeCheckResolverRef.current = null;
          await stopRecordingWithoutHandleNext();
          const result = await askForMoreTime();
          resolve(result);
        }
      }, 500);
      moreTimeCheckIntervalRef.current = checkResponse;
    });
  };

  const isRequestingMoreTime = (transcript) => {
    const timeRequestPhrases = [
      "need more time",
      "give me time",
      "one moment",
      "need a moment",
      "need some time",
      "more time"
    ];
    return timeRequestPhrases.some(phrase =>
      transcript.toLowerCase().includes(phrase)
    );
  };

  const isRequestingRepeatQuestion = (transcript) => {
    const timeRequestPhrases = [
      "repeat the question",
      "repeat question",
      "repeat this question",
      "could you say the question again",
      "say the question again",
      "what was the question",
      "can you say that question again",
      "i didn't hear the question",
      "can you repeat that",
      "repeat that question",
      "sorry, what was the question",
      "can you say that again",
      "please repeat",
      "repeat again",
      "say that again",
      "come again",
      "repeat please"
    ];
    return timeRequestPhrases.some(phrase =>
      transcript.toLowerCase().includes(phrase)
    );
  };

  // Add this function to detect next question requests
const isRequestingNextQuestion = (transcript) => {
  const nextQuestionPhrases = [
    "next question",
    "that's it",
    "thats it",
    "don't remember",
    "i am done",
    "i'm done",
    "move to next question",
    "next one",
    "next please",
    "skip this question",
    "skip question",
    "move on",
    "let's move on",
    "i'm finished",
    "i've finished",
    "i have finished",
    "that's all",
    "that will be all",
    "nothing more to add",
    "end this question",
    "complete this question",
    "done with this question",
    "ready for next question",
    "proceed to next",
    "advance to next question",
    "next part",
    "skip",
    "move forward",
    "don't know",
    "do not know",
    "no idea",
    "dont know",
    "nahi pata",
    "nahi pta"
  ];
  
  return nextQuestionPhrases.some(phrase =>
    transcript.toLowerCase().includes(phrase)
  );
};

  // Handle Next Question
const handleNextQuestion = async () => {
  setTimeLeft(180); // 3 minutes — matches the initial timer in useState above.

  clearTimeout(thinkingTimeoutRef.current);

  requestMadeRef.current = false;
  recordingMoreTimeoutRef.current = false;
  // Reset per-question counters so every question gets a fresh "more time?" and "are you there?" flow
  moreAreYouThereRef.current = 0;
  retryAttemptsRef.current = 0;

  // Kill any in-flight "do you need more time?" / "Are you there?" poll
  // BEFORE we advance. Without this, a still-polling interval from the
  // previous question would eventually play "Are you there?" (or the
  // "confirm your presence" prompt) OVER the next question's audio.
  // Bug reproducer: silence detected → "do you need more time?" prompt
  // → few seconds pass → next question starts reading → simultaneously
  // the stale poll's idle-timeout fires and plays audio4/audio5.
  cancelMoreTimeCheck();

  // await stopRecording();

  stopMonitorAudio();
  stopLiveMonitoring();
  stopNonStopLiveMonitoring();

  if (currentQuestionIndexRef.current === 0) {
    await playAudioAndWait("audio7");
  }

  // If we just finished the last question, do NOT advance / auto-end.
  // Instead, switch the button to "Submit Interview" and tell the candidate to click it.
  // This avoids the fragile silence-detection + useEffect auto-end chain that has caused stuck-on-Q15 hangs.
  const nextIndex = currentQuestionIndexRef.current + 1;
  if (nextIndex >= totalQuestions) {
    console.log('[interview] Last question answered — prompting candidate to click Submit. nextIndex:', nextIndex, 'totalQuestions:', totalQuestions);
    setReadyToSubmit(true);
    isProcessingNextQuestionRef.current = false;
    try {
      await speak("Your interview is complete. Please click the Submit Interview button to finish.");
    } catch (e) {
      console.warn('[interview] completion prompt speak failed (non-fatal):', e);
    }
    return;
  }

  currentQuestionIndexRef.current = nextIndex;
  technicalQuestionIndexRef.current += 1;
  setDummyState((prev) => prev + 1);
  transcriptionRef.current = "";

  // Reset the processing flag when starting a new question
  // (delayed slightly to ensure all async operations are complete)
  setTimeout(() => {
    isProcessingNextQuestionRef.current = false;
  }, 1000);
};

  // Handle Stop Interview
  const handleStopInterview = async () => {
    console.log('[interview] handleStopInterview called');
    setShowConfirmEndModal(false);
    if (hasEnded) return; // 👈 Prevent multiple clicks
    setHasEnded(true);    // ✅ Block future clicks

    // Disable all prompts immediately
    window.onbeforeunload = null;
    window.onpopstate = null;
    window.onunload = null;

    interviewActiveRef.current = false;
    clearInterval(countdownIntervalRef.current);
    
    // Force cleanup of audio context and recognizer since interview is ending
    cleanupAudioContext(true);
    await cleanupRecognizer(true);
    console.log('[interview] Audio context and recognizer cleaned up');

    const conversationToSend = (conversationRef.current || []).map((m, idx) => ({
      role: m.role || (m.system ? 'system' : 'user'),
      text: m.text || '',
      id: m.id || `msg-${Date.now()}-${idx}`,
      timestamp: m.timestamp || Date.now()
    }));

    // Upload session audio BEFORE scoring so audio_analysis() finds the file
    await stopSessionRecordingAndUpload();

    fetch(`/api/save_score_in_bg?test_id=${test_id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        test_type: testType,
        conversation: conversationToSend
      })
    });


    if (testType === "fscp") {
      await playAudioAndWait("audio14"); 
      // await speak("Congratulations your Personality and Behavioral assessment is now complete. Let's proceed to Coding test.");
      const destination = `/icoding?test_id=${test_id}`;
      console.log("Coding Questions", codingQuestions);
      navigate(destination, {
        state: { 
          questions: codingQuestions, 
          resumeQuestions, 
          testType, 
          backendLanguage, 
          isVeryHard,
          codingQuestions,
          screenSharingActive: true
        }
      });
      return;
    } else if (testType === "fsc" || testType === "fscc") {
        await playAudioAndWait("audio15"); 
        // await speak("Your screening test is completed. Let's begin your coding test.");
        const destination = `/icoding?test_id=${test_id}`;
        console.log("Coding Questions", codingQuestions);
        navigate(destination, {
          state: { 
            questions: codingQuestions, 
            resumeQuestions, 
            testType, 
            backendLanguage, 
            isVeryHard,
            codingQuestions,
            screenSharingActive: true
          }
        });
        return;
    }

    // Run stopRecording + playAudioAndWait simultaneously
    if (isRecording) {
      await Promise.all([
        stopRecording(),
        stopScreenRecording(),
        playAudioAndWait("audio8")
      ]);
    } else {
      await Promise.all([
        stopScreenRecording(),
        playAudioAndWait("audio8"),
      ]);
    }
    // await speak2("Thank you for your time today, It was a pleasure speaking with you and learning more about your experience. Before you go, we’d appreciate it if you could provide us with feedback on your interview experience by giving us a rating on the next page.");

    // Stop all media streams
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    // Stop desktop proctoring and submit data
    if (isDesktopApp()) {
      console.log("[Desktop] Stopping proctoring and submitting data...");
      try {
        const proctoringResult = await stopDesktopProctoring();
        console.log("[Desktop] Proctoring stopped:", proctoringResult);

        // Submit proctoring data to backend
        await submitProctoringData(test_id, testType);
        console.log("[Desktop] Proctoring data submitted");

        // Upload screen recording to S3
        console.log("[Desktop] Uploading screen recording...");
        const uploadResult = await uploadRecording(test_id);
        console.log("[Desktop] Recording upload result:", uploadResult);
      } catch (error) {
        console.error("[Desktop] Error stopping proctoring:", error);
      }
    }

    navigate("/user-home", {
      state: {
        showTestSubmittedPopup: true
      }
    });

    // window.opener = null;
    // window.close();
  };
  
  // UseEffect to handle question change and speaking
  useEffect(() => {
    // Guard: Ensure we have valid questions data before proceeding
    if (!questions || questions.length === 0) {
      console.warn("Questions not loaded yet, skipping question handling");
      return;
    }
    
    // Guard: Don't end interview on initial mount - only process if test has started
    // or if dummyState > 0 (meaning we've progressed through questions)
    if (!testStarted && dummyState === 0) {
      // Initial mount without testStarted - wait for testStarted to be set
      return;
    }
    
    if (currentQuestionIndexRef.current < totalQuestions) {
      retryAttemptsRef.current = 0; // Reset the retry attempts
      getQuestionText();
    } else if (testStarted) {
      // Only end interview if test was actually started
      handleStopInterview();
    }
  }, [dummyState, testStarted]);

  // Handle screen recording state when QuestionSection loads
  useEffect(() => {
    // Get screen recording state from global manager
    const recordingState = screenRecordingManager.getScreenRecordingState();
    
    console.log("QuestionSection loaded - Screen recording state:", recordingState);

    // If screen recording is active from TestingSection, restore the state
    if (recordingState.isScreenRecording && recordingState.screenSharingActive) {
      // Restore refs
      screenStreamRef.current = recordingState.screenStreamRef;
      screenRecorderRef.current = recordingState.screenRecorderRef;
      screenChunksRef.current = recordingState.screenChunksRef;
      micStreamRef.current = recordingState.micStreamRef;
      
      // Restore state
      setIsScreenRecording(recordingState.isScreenRecording);
      setScreenSharingActive(recordingState.screenSharingActive);
      setScreenShareError(recordingState.screenShareError);
      setTestStarted(recordingState.testStarted);
      
      // Request fullscreen when restoring screen recording
      handleReturnToFullscreen();
      
      console.log("Screen recording restored from TestingSection - starting interview");
    }
  }, []);

  // UseEffect to handle question change and speaking
  useEffect(() => {
    const handleSpeakingAndRecording = async () => {
      await handleQuestionSpeech(); // Wait for the speech to finish before starting recording
    };
    if (testStarted) {
      handleSpeakingAndRecording();
    }
  }, [question]);

  useEffect(() => {
    // Fetch user data
    requestQueue.queueHighPriorityRequest(() =>
      fetch('/api/user')
        .then(response => response.json())
        .then(data => {
          setUserName(data.user_name); // Set user data
          setLoading(false); // Data loaded, stop loading
        })
        .catch(() => {
          setLoading(false); // Stop loading on error
        })
    );

    startStreaming();

    return () => {
      console.log('[audio] Component cleanup running');
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      // Note: Don't cleanup audio context here - React Strict Mode will remount
      // Audio context cleanup happens in handleStopInterview or on page unload
      window.onbeforeunload = null;
    };
  }, []);

  // Cleanup audio on actual page unload (not React Strict Mode)
  // Also fires sendBeacon so audio is saved even if the user closes the tab mid-test
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('[audio] Page unloading - cleaning up');
      cleanupAudioContext(true);
      cleanupRecognizer(true);

      // ── Best-effort audio beacon on tab/browser close ──────────────────────
      // fetch() is killed by the browser on unload; sendBeacon() survives.
      // requestData() flushes the current in-progress chunk before we read it.
      const recorder = sessionRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.requestData(); } catch (_) {}
        try { recorder.stop(); }       catch (_) {}
      }
      const chunks = sessionChunksRef.current;
      if (chunks && chunks.length > 0) {
        try {
          const blob = new Blob(chunks, { type: sessionMimeRef.current || 'audio/webm' });
          // sendBeacon has a per-browser size cap (~64 KB guaranteed, often much more).
          // For short/abandoned sessions this usually succeeds; long sessions fall back
          // to the text-metric analyser automatically on the server side.
          const form = new FormData();
          form.append('audio',   blob, 'interview.webm');
          form.append('test_id', test_id || '');
          const sent = navigator.sendBeacon('/api/participant/audio', form);
          console.log('[session/audio] sendBeacon on unload —', sent ? 'queued' : 'rejected (payload too large)');
        } catch (err) {
          console.error('[session/audio] sendBeacon error:', err);
        }
      }

      // ── SCREEN RECORDING RECOVERY on abandonment ─────────────────────
      // Signal the backend "this candidate is walking away — please
      // merge whatever chunks you already have into S3". The backend
      // recovery worker picks up this hurry marker on its next tick
      // (typically within 10 minutes) and does the actual merge.
      //
      // We DON'T send the screen chunks via sendBeacon here — they're
      // already on the server disk (uploaded during the test via
      // upload_screen_chunk). We only need to tell the server to
      // ACT on them. That fits comfortably in the sendBeacon budget.
      try {
        if (test_id && navigator.sendBeacon) {
          const triggerForm = new FormData();
          triggerForm.append('test_id', String(test_id));
          const triggered = navigator.sendBeacon('/api/trigger_merge_async', triggerForm);
          console.log('[screen/merge] sendBeacon on unload —', triggered ? 'queued' : 'rejected');
        }
      } catch (err) {
        console.error('[screen/merge] sendBeacon error:', err);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // Track if this is a real unmount vs React Strict Mode cleanup
    let isRealUnmount = false;
    
    // Set flag after a delay - if cleanup runs before this, it's Strict Mode
    const timeoutId = setTimeout(() => {
      isRealUnmount = true;
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      window.onbeforeunload = null;
      window.onpopstate = null;
      window.onunload = null;
      
      // Only clear screen recording state on real unmount, not React Strict Mode cleanup
      // In Strict Mode (dev), cleanup runs immediately before remount
      // We use a small delay check - if cleanup runs within 100ms of mount, it's likely Strict Mode
      if (isRealUnmount) {
        console.log('[audio] Real unmount detected - cleaning up audio and recognizer');
        cleanupAudioContext(true);
        cleanupRecognizer(true);
        screenRecordingManager.clearScreenRecordingState();
      }
    };
  }, []);

  useEffect(() => {
    if (isRecording) {
      // Reset timer when starting recording
      // setTimeLeft(90);
      
      // Start countdown timer
      timerRef.current = setInterval(() => {
        setTimeLeft(prevTime => {
          if (!isRecording) return timeLeft;
          if (prevTime === 1) {
            clearInterval(timerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
              handleRecordingTimeout();// This will trigger the usual stop flow
            }
            return 0;
          }
          if (prevTime === 0) return 0;
          return prevTime - 1;
        });
      }, 1000);
    } else {
      // Clear timer when not recording
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [convTick, question]);

  const addAssistantMessage = (text, { timeWindowMs = 60000 } = {}) => {
    if (!text || !text.trim()) return;

    // Normalize text for comparison (trim + collapse whitespace + lowercase)
    const normalize = (s) => s.replace(/\s+/g, ' ').trim().toLowerCase();
    const newNorm = normalize(text);

    // Find the most recent assistant message (scan from end)
    let lastAssistant = null;
    for (let i = conversationRef.current.length - 1; i >= 0; i--) {
      const it = conversationRef.current[i];
      if (it && it.role === 'assistant') {
        lastAssistant = it;
        break;
      }
    }

    if (lastAssistant) {
      const lastNorm = normalize(lastAssistant.text || '');
      // If same normalized text, skip duplicate
      if (lastNorm === newNorm) {
        // Optionally also check a short time-window to tolerate replays after a long time.
        const age = Date.now() - (lastAssistant.ts || 0);
        if (age < timeWindowMs) {
          // console.log('Skipped duplicate assistant message (recent identical).');
          return;
        }
        // if older than timeWindowMs, we allow re-adding (rare cases)
      }
    }

    const entry = {
      id: `a-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
      role: 'assistant',
      text,
      ts: Date.now()
    };
    conversationRef.current.push(entry);
    // setDummyState((s)=>s+1);
  };

  // small helper to push user messages (committed transcription)
  const addUserMessage = (text) => {
    if (!text || !text.trim()) return;
    const entry = {
      id: `u-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
      role: 'user',
      text,
      ts: Date.now()
    };
    conversationRef.current.push(entry);
    // setDummyState((s)=>s+1);
  };

  // Format time helper for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };


  // Render chat messages with new styled components
  function renderChatMessages() {
    const messages = conversationRef.current;

    if (messages.length === 0 && !recognisingText) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <MessageSquare className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">No messages yet</p>
          <p className="text-muted-foreground/70 text-xs mt-1">Conversation will appear here</p>
        </div>
      );
    }

    return (
      <>
        {messages.map((m, idx) => {
          const role = (m.role || "").toLowerCase();

          if (role === "assistant") {
            return (
              <div key={m.id || `ai-${idx}`} className="animate-in fade-in duration-200">
                <p className="text-xs text-muted-foreground mb-1">Sana</p>
                <div className="bg-muted text-foreground px-3 py-2 rounded-lg text-sm leading-relaxed">
                  {m.text}
                </div>
              </div>
            );
          }

          // user message
          return (
            <div key={m.id || `user-${idx}`} className="animate-in fade-in duration-200">
              <p className="text-xs text-muted-foreground mb-1 text-right">{userName}</p>
              <div className="bg-primary text-primary-foreground px-3 py-2 rounded-lg text-sm leading-relaxed ml-auto max-w-[90%]">
                {m.text}
              </div>
            </div>
          );
        })}

        {/* Live user transcription */}
        {recognisingText && (
          <div className="animate-in fade-in duration-200">
            <p className="text-xs text-muted-foreground mb-1 text-right flex items-center justify-end gap-2">
              {userName || "You"}
              <span className="text-success text-[10px]">Speaking...</span>
            </p>
            <div className="bg-primary/90 text-primary-foreground px-3 py-2 rounded-lg text-sm leading-relaxed ml-auto max-w-[90%] border border-primary">
              {recognisingText}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="dark h-screen w-full bg-background flex flex-col overflow-hidden">
      <Toaster position="top-center" richColors />
      {/* Browser-side proctoring (MediaPipe detectors + snapshot evidence),
          reusing the webcam startStreaming() already opened. openOwnCamera is
          false so we never race QuestionSection into a second getUserMedia. */}
      <ProctoringMount
        testId={test_id}
        stream={proctorStream}
        active={!hasEnded}
        openOwnCamera={false}
      />
      {/* ===== TOP HEADER BAR - Zoom Style ===== */}
      <header className="flex items-center justify-between px-6 py-3 backdrop-blur-sm border-b border-border min-h-[56px] flex-shrink-0 bg-card/50">
        {/* Left: Title + Question Counter */}
        <div className="flex items-center gap-3">
          <span className="text-foreground font-medium">AI Interview</span>
          <Badge variant="secondary" className="text-xs">
            Question {currentQuestionIndexRef.current + 1} of {totalQuestions}
          </Badge>
        </div>

        {/* Center: Timer */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border">
          <Clock className="w-4 h-4 text-foreground" />
          <span
            className={cn(
              "font-mono font-semibold text-sm tabular-nums",
              timeLeft <= 10 ? "text-destructive" : timeLeft <= 30 ? "text-warning" : "text-foreground"
            )}
          >
            {formatTime(timeLeft)}
          </span>
        </div>

        {/* Right: Network Status */}
        <div className="flex items-center gap-2">
          <NetworkIcon
            className={cn(
              "w-4 h-4",
              networkStatus === "good"
                ? "text-success"
                : networkStatus === "fair"
                ? "text-warning"
                : "text-destructive"
            )}
          />
          <span className="text-muted-foreground text-xs">
            {networkSpeed ? `${parseFloat(networkSpeed).toFixed(1)} Mbps` : "—"}
          </span>
        </div>
      </header>

      {/* ===== MAIN CONTENT AREA ===== */}
      <div className="flex-1 flex overflow-hidden bg-background">
        {/* Video Grid Section - Zoom style 50-50 layout */}
        <div
          className={cn(
            "flex-1 flex items-center justify-center p-6 transition-all duration-300",
            showTranscript ? "lg:mr-0" : ""
          )}
        >
          {/* Video Container - centered with max height */}
          <div className="flex gap-3 w-full max-w-6xl h-[60vh] max-h-[500px]">
            {/* AI Interviewer Video (Left) */}
            <div className="flex-1 relative rounded-lg overflow-hidden bg-card border-2 border-border">
              {/* Energy Visualizer as AI "video" */}
              <div className="w-full h-full">
                <EnergyVisualizer
                  isSpeaking={isSpeaking}
                  isRecording={isRecording}
                  timeLeft={timeLeft}
                  showTimer={false}
                />
              </div>

              {/* AI Name Badge */}
              <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-background/60 backdrop-blur-sm px-3 py-1.5 rounded">
                <span className="text-foreground text-sm font-medium">Sana</span>
                <span className="text-muted-foreground text-xs">
                  {isSpeaking ? "Speaking" : isRecording ? "Listening" : "Thinking"}
                </span>
                {isSpeaking && <span className="w-2 h-2 rounded-full bg-success animate-pulse" />}
              </div>

              {/* Speaking indicator border */}
              {isSpeaking && (
                <div className="absolute inset-0 border-2 border-success rounded-lg pointer-events-none" />
              )}
            </div>

            {/* Candidate Video (Right) */}
            <div className="flex-1 relative rounded-lg overflow-hidden bg-card border-2 border-border">
              <video ref={videoRef} autoPlay muted className="w-full h-full object-cover" />

              {/* Live voice-activity waveform — pulses in real time with mic
                  input so the candidate has visual proof their voice is
                  being captured. Only drawn while actively recording so the
                  60fps raf loop doesn't run between questions. */}
              {isRecording && (
                <div className="absolute top-3 left-3 flex items-center gap-2 bg-background/60 backdrop-blur-sm px-2 py-1 rounded">
                  <VoiceWaveform analyserRef={analyserRef} active={isRecording} />
                </div>
              )}

              {/* Thinking-time countdown badge — removed intentionally. VAD's
                  minSpeechMs=500 guard already makes the pre-speech pause
                  unnecessary; the countdown felt artificial and non-realtime. */}

              {/* Live interim transcript caption — subtitle-style overlay
                  showing Azure's `recognizing` (interim) text word-by-word.
                  Positioned above the name badge; auto-hides on final text
                  commit or when the field goes empty between questions. */}
              {recognisingText && (
                <div className="absolute inset-x-3 bottom-14 flex justify-center pointer-events-none animate-in fade-in duration-150">
                  <div className="max-w-full bg-background/75 backdrop-blur-md text-foreground px-3 py-1.5 rounded-md text-sm italic text-center shadow-md line-clamp-2">
                    {recognisingText}
                  </div>
                </div>
              )}

              {/* Candidate Name Badge */}
              <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-background/60 backdrop-blur-sm px-3 py-1.5 rounded">
                <span className="text-foreground text-sm font-medium">{userName || "Candidate"}</span>
                <span className="text-muted-foreground text-xs">
                  {isRecording || recognisingText ? "Speaking" : "Listening"}
                </span>
                {(isRecording || recognisingText) && (
                  <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                )}
              </div>

              {/* Speaking indicator border */}
              {(isRecording || recognisingText) && (
                <div className="absolute inset-0 border-2 border-success rounded-lg pointer-events-none" />
              )}
            </div>
          </div>
        </div>

        {/* ===== TRANSCRIPT PANEL (Collapsible) - Zoom Chat Style ===== */}
        <div
          className={cn(
            "bg-card border-l border-border flex flex-col transition-all duration-300 overflow-hidden",
            showTranscript ? "w-full lg:w-80" : "w-0"
          )}
        >
          {showTranscript && (
            <>
              {/* Transcript Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  <h3 className="text-foreground font-medium text-sm">Transcript</h3>
                  {isRecognising && (
                    <span className="flex items-center gap-1 text-success text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                      Listening
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowTranscript(false)}
                  className="w-6 h-6 rounded flex items-center justify-center hover:bg-accent transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Chat Messages */}
              <div ref={chatBodyRef} className="flex-1 overflow-y-auto p-3 space-y-3 scroll-smooth">
                {renderChatMessages()}
              </div>

              {/* Error Banner */}
              {errorMessage && (
                <div className="mx-3 mb-3 p-2 bg-destructive/30 border border-destructive rounded flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                  <span className="text-destructive-foreground text-xs">{errorMessage}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ===== BOTTOM CONTROL BAR ===== */}
      <footer className="flex items-center justify-center gap-6 px-6 py-3 bg-card/95 border-t border-border">
        {/* Status Indicators */}
        <div className="flex items-center gap-4">
          {/* Mic Status Indicator */}
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center bg-success/20",
                isRecording || recognisingText ? "animate-pulse" : ""
              )}
            >
              <Mic className="w-4 h-4 text-success" />
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-medium text-success">Microphone</p>
              <p className="text-[10px] text-muted-foreground">
                {isRecording || recognisingText ? "Listening" : "Ready"}
              </p>
            </div>
          </div>

          {/* Camera Status Indicator */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-success/20">
              <Video className="w-4 h-4 text-success" />
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-medium text-success">Camera</p>
              <p className="text-[10px] text-muted-foreground">Active</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-border" />

        {/* Toggle Conversation */}
        <button
          onClick={() => setShowTranscript(!showTranscript)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
            showTranscript
              ? "bg-primary text-primary-foreground"
              : "bg-muted hover:bg-accent text-foreground"
          )}
        >
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm hidden sm:block">Transcript</span>
        </button>

        {/* End Interview / Submit Interview Button */}
        <button
          onClick={() => readyToSubmit ? handleStopInterview() : setShowConfirmEndModal(true)}
          disabled={hasEnded}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50",
            readyToSubmit
              ? "bg-green-600 hover:bg-green-700 text-white animate-pulse"
              : "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          )}
        >
          <PhoneOff className="w-4 h-4" />
          <span>{hasEnded ? "Ending..." : readyToSubmit ? "Submit Interview" : "End Interview"}</span>
        </button>
      </footer>

      {/* ===== END INTERVIEW CONFIRMATION DIALOG ===== */}
      <AlertDialog open={showConfirmEndModal} onOpenChange={setShowConfirmEndModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Interview?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end this interview? This action cannot be undone and your
              responses will be submitted for review.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStopInterview}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, End Interview
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ===== PROCTORING WARNINGS ===== */}
      {/* Tab Switch Warning */}
      {showTabSwitchWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-warning bg-warning/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-warning" />
            <div>
              <p className="font-semibold text-foreground">Tab switching detected!</p>
              <p className="text-sm text-muted-foreground">This is violation {switchCount} of 3</p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowTabSwitchWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Fullscreen Warning */}
      {showFullscreenWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-destructive bg-destructive/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-destructive" />
            <div>
              <p className="font-semibold text-foreground">Please return to fullscreen!</p>
              <p className="text-sm text-muted-foreground">This is violation {fullscreenExitCount} of 3</p>
            </div>
            <Button variant="outline" size="sm" className="ml-2" onClick={handleReturnToFullscreen}>
              Return
            </Button>
          </Card>
        </div>
      )}

      {/* Paste Warning */}
      {showPasteWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-destructive bg-destructive/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-destructive" />
            <div>
              <p className="font-semibold text-foreground">Copy/paste is not allowed!</p>
              <p className="text-sm text-muted-foreground">This is violation {pasteAttempts} of 3</p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowPasteWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Phone Detection Warning */}
      {showPhoneWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-destructive bg-destructive/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-destructive" />
            <div>
              <p className="font-semibold text-foreground">Mobile phone detected!</p>
              <p className="text-sm text-muted-foreground">
                Remove all electronic devices immediately. Warning {phoneDetectionWarnings} of 2
              </p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowPhoneWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Head Pose Warning */}
      {showHeadPoseWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-warning bg-warning/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-warning" />
            <div>
              <p className="font-semibold text-foreground">Head Position Violation!</p>
              <p className="text-sm text-muted-foreground">
                Head turned {headPoseDirection}. Please face the screen directly. Warning {headPoseWarnings} of 3
              </p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowHeadPoseWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Suspicious Object Warning */}
      {showSuspiciousObjectWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-warning bg-warning/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-warning" />
            <div>
              <p className="font-semibold text-foreground">Suspicious object detected!</p>
              <p className="text-sm text-muted-foreground">
                Clear your workspace of unauthorized materials. Warning {suspiciousObjectWarnings} of 3
              </p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowSuspiciousObjectWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Eye Tracking Warning */}
      {showEyeTrackingWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-warning bg-warning/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-warning" />
            <div>
              <p className="font-semibold text-foreground">Please keep your eyes on the screen!</p>
              <p className="text-sm text-muted-foreground">Warning {eyeTrackingWarnings} of 3</p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowEyeTrackingWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Multiple Faces Warning */}
      {showMultipleFacesWarning && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 duration-300">
          <Card className="flex items-center gap-3 px-6 py-4 shadow-2xl border-destructive bg-destructive/10">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 text-destructive" />
            <div>
              <p className="font-semibold text-foreground">Multiple people detected!</p>
              <p className="text-sm text-muted-foreground">
                Please ensure you are alone. Warning {multipleFacesWarnings} of 3
              </p>
            </div>
            <Button variant="ghost" size="icon" className="ml-2" onClick={() => setShowMultipleFacesWarning(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      )}

      {/* Face Not Visible Modal */}
      {showFaceNotVisibleWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Face Not Visible</h3>
            <p className="text-muted-foreground mb-4">
              Please adjust your camera position and ensure proper lighting conditions.
            </p>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full border-4 border-warning flex items-center justify-center">
                <span className="text-2xl font-bold text-warning">{faceNotVisibleTimer}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">seconds remaining</p>
            <Button onClick={handleFaceVisibleAgain} className="w-full">
              I'm Ready - Resume Test
            </Button>
          </Card>
        </div>
      )}

      {/* Desktop app real-time violation warning */}
      <DesktopViolationWarning enabled={testStarted} />
    </div>
  );
}

export default QuestionSection;
